import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import useAccessControl from "../context/useAccessControl";
import {
  cancelInvitation,
  createAdminShift,
  createBranch,
  createInternalUserInvitation,
  deactivateCompanyUser,
  deleteInvitation,
  deleteBranch,
  deleteAdminShift,
  getCompanyProfile,
  listAdminShifts,
  listCompanyInvitations,
  listCompanyUsers,
  updateCompanyUserAccess,
  updateInternalUserInvitation,
  updateBranchKitchenMode,
  updateBranchKioskConfig,
  updateCompanyProfile,
} from "../../services/supabase/companyAdminService";
import {
  assignSharedMenuToBranch,
  createCustomMenuForBranch,
  getBranchesMenuSetup,
} from "../../services/supabase/menuService";
import {
  getRestaurantProfile,
  normalizeKioskStationCount,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  buildInvitationRegistrationUrl,
  copyTextToClipboard,
} from "../../services/local/invitationLinks";
import { buildKioskAndroidLauncherUrl } from "../../services/local/kioskLauncher";
import mozzoKioskDebugApkUrl from "../../android/mozzo-kiosk/app/build/outputs/apk/debug/app-debug.apk";
import {
  buildPublicDisplayUrl,
  buildPublicKitchenUrl,
  getBranchScreenPairingCodes,
  rotateBranchScreenPairingCode,
} from "../../services/supabase/qrService";
import {
  fetchSharedOperationalState,
  getActiveKioskTerminalClaims,
  pushSharedOperationalState,
} from "../../services/supabase/operationalStateService";
import BranchDigitalPaymentsPanel from "../common/BranchDigitalPaymentsPanel";
import ArcaFiscalConnectionPanel from "../common/ArcaFiscalConnectionPanel";
import BranchOperationalModulesPanel from "../common/BranchOperationalModulesPanel";
import RestaurantContractReceipts from "../common/RestaurantContractReceipts";
import RestaurantLifecyclePanel from "../common/RestaurantLifecyclePanel";
import {
  buildMozzoRoleBranchPermissions,
  getMozzoInternalUserRole,
  getMozzoInternalUserRoleLabel,
  getMozzoInternalUserRoleModuleKeys,
  MOZZO_BRANCH_PERMISSION_GROUPS,
  MOZZO_BRANCH_PERMISSION_KEYS,
  MOZZO_MODULE_DEFINITIONS,
  resolveMozzoInternalUserRoleKey,
  updateMozzoBranchPermissionGroup,
} from "../../services/local/accessControl";
import {
  COMPANY_FISCAL_MODES,
  COMPANY_FISCAL_MODE_OPTIONS,
  DEFAULT_COMPANY_FISCAL_MODE,
  getCompanyFiscalModeOption,
} from "../../services/local/fiscalSettings";
import {
  applyBranchOperationalModuleToggle,
  getBranchOperationalModulesError,
} from "../../services/local/branchOperationalModules";

const dayOptions = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miercoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sabado" },
];

const KITCHEN_MODE_OPTIONS = [
  {
    id: "manual",
    label: "Manual",
    description:
      "El pedido queda visible para que el equipo lo pase a cocina a mano.",
    badge: "Manual",
  },
  {
    id: "printer",
    label: "Impresora",
    description:
      "La cocina recibe comandas impresas. En kiosk, el display publico sigue mostrando numeros en preparacion o listos para retirar.",
    badge: "Impresora",
  },
  {
    id: "screen",
    label: "Pantalla",
    description:
      "Los pedidos se preparan desde una pantalla dedicada para cocina.",
    badge: "Pantalla",
  },
];

const KIOSK_EXPERIENCE_OPTIONS = [
  {
    id: "disabled",
    label: "Sin kiosk",
    badge: "Panel habitual",
    serviceMode: "salon",
    kioskMenuMode: "current",
    description:
      "La sucursal opera con el flujo normal de salon, QR y mozo.",
  },
  {
    id: "current",
    label: "Kiosk con carta actual",
    badge: "Kiosk activo",
    serviceMode: "kiosk",
    kioskMenuMode: "current",
    description:
      "El kiosk usa la carta que hoy ya tiene cargada esta sucursal.",
  },
  {
    id: "custom",
    label: "Kiosk con carta nueva",
    badge: "Kiosk + carta propia",
    serviceMode: "kiosk",
    kioskMenuMode: "custom",
    description:
      "El kiosk trabaja con una carta nueva de la sucursal para personalizarla aparte.",
  },
];

const BRANCH_SERVICE_MODE_OPTIONS = [
  {
    id: "salon",
    label: "Restaurante",
    badge: "Salon clasico",
    description:
      "La sucursal opera con mesas, QR, mozo y flujo tradicional de salon.",
  },
  {
    id: "kiosk",
    label: "Kiosk",
    badge: "Autoservicio",
    description:
      "La sucursal trabaja con totems, pedido por nombre y numero de preparacion.",
  },
];

const resolveKioskOptionId = (profile) => {
  if (profile?.serviceMode !== "kiosk") {
    return "disabled";
  }

  return profile?.kioskMenuMode === "custom" ? "custom" : "current";
};

const resolveBranchServiceMode = (branch, profile = null) => {
  if (branch?.serviceMode === "kiosk" || branch?.service_mode === "kiosk") {
    return "kiosk";
  }

  if (branch?.serviceMode === "salon" || branch?.service_mode === "salon") {
    return "salon";
  }

  return profile?.serviceMode === "kiosk" ? "kiosk" : "salon";
};

const resolveBranchKioskMenuMode = (branch, profile = null) =>
  branch?.kioskMenuMode === "custom" || branch?.kiosk_menu_mode === "custom"
    ? "custom"
    : profile?.kioskMenuMode === "custom"
      ? "custom"
      : "current";

const resolveBranchKioskStationCount = (branch, profile = null) =>
  normalizeKioskStationCount(
    branch?.kioskStationCount ??
      branch?.kiosk_station_count ??
      profile?.kioskStationCount
  );

const buildResolvedBranchOperationalProfile = (branch, profile = null) => {
  const baseProfile = profile || getRestaurantProfile(branch);
  const serviceMode = resolveBranchServiceMode(branch, baseProfile);

  return {
    ...baseProfile,
    serviceMode,
    kioskMenuMode: resolveBranchKioskMenuMode(branch, baseProfile),
    kioskStationCount: resolveBranchKioskStationCount(branch, baseProfile),
    kitchenMode: normalizeKitchenModeForServiceMode(
      serviceMode,
      branch?.kitchenMode ??
        branch?.kitchen_mode ??
        baseProfile?.kitchenMode ??
        "manual"
    ),
  };
};

const initialBranchForm = {
  name: "",
  phone: "",
  address: "",
  description: "",
  serviceMode: "salon",
  salonEnabled: true,
  deliveryEnabled: false,
  wePedidosEnabled: false,
  waiterEnabled: true,
  stockEnabled: true,
  aiEnabled: true,
  kioskMenuMode: "current",
  kioskStationCount: 1,
};

const BRANCH_MODULE_FORM_OPTIONS = [
  {
    key: "salonEnabled",
    title: "Salon y mesas",
    description: "Mesas, QR, reservas y atencion presencial.",
  },
  {
    key: "deliveryEnabled",
    title: "Delivery y retiro",
    description: "Pedidos con direccion, retiro y seguimiento.",
  },
  {
    key: "wePedidosEnabled",
    title: "We! pedidos",
    description: "Publicacion en We! pedidos y acceso a riders.",
  },
  {
    key: "waiterEnabled",
    title: "Equipo de mozos",
    description: "Accesos de mozo y llamados desde la mesa.",
  },
  {
    key: "stockEnabled",
    title: "Stock e ingredientes",
    description: "Validacion y descuento automatico de insumos.",
  },
  {
    key: "aiEnabled",
    title: "IA / mozo virtual",
    description: "Asistencia y sugerencias dentro de la carta.",
  },
];

const KIOSK_KITCHEN_MODE_IDS = new Set(["printer", "screen"]);
const EMPTY_OPERATIONAL_STATE = () => ({
  mesasActivas: [],
  pedidosHistoricos: [],
});

const normalizeKitchenModeForServiceMode = (serviceMode, kitchenMode) => {
  if (serviceMode === "kiosk") {
    return KIOSK_KITCHEN_MODE_IDS.has(kitchenMode) ? kitchenMode : "screen";
  }

  return kitchenMode === "screen" || kitchenMode === "printer" ? kitchenMode : "manual";
};

const getKitchenModeOptionsForServiceMode = (serviceMode) =>
  serviceMode === "kiosk"
    ? KITCHEN_MODE_OPTIONS.filter((option) => KIOSK_KITCHEN_MODE_IDS.has(option.id))
    : KITCHEN_MODE_OPTIONS;

const buildLauncherSafeName = (value) =>
  String(value || "Sucursal")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "Sucursal";

const buildKioskLauncherFileName = (branchName) =>
  `MozzoApp-Kiosk-${buildLauncherSafeName(branchName)}.cmd`;

const buildKioskWindowsLauncherContent = ({ baseUrl, branchId }) => `@echo off
setlocal
set "BASE_URL=${baseUrl}"
set "BRANCH_ID=${branchId}"
set "PACKAGE_VERSION=2026.07.01-sta1"
set "INSTALL_DIR=%LOCALAPPDATA%\\Programs\\MozzoApp Kiosk"
set "LAUNCHER_PATH=%INSTALL_DIR%\\Mozzo.Kiosk.Windows.exe"
set "VERSION_FILE=%INSTALL_DIR%\\.mozzo-kiosk-version"

set "INSTALLED_VERSION="
if exist "%VERSION_FILE%" set /p INSTALLED_VERSION=<"%VERSION_FILE%"
if exist "%LAUNCHER_PATH%" if "%INSTALLED_VERSION%"=="%PACKAGE_VERSION%" goto ensure_runtime

echo Preparando MozzoApp Kiosk para Windows...
if not exist "%INSTALL_DIR%" mkdir "%INSTALL_DIR%"
set "MOZZO_INSTALL_DIR=%INSTALL_DIR%"
set "MOZZO_ZIP_URL=%BASE_URL%/downloads/mozzo-kiosk-windows/mozzo-kiosk-windows.zip"
set "MOZZO_ZIP_PATH=%TEMP%\\mozzo-kiosk-windows.zip"
powershell -NoProfile -ExecutionPolicy Bypass -Command "$ProgressPreference='SilentlyContinue'; Invoke-WebRequest -UseBasicParsing -Uri $env:MOZZO_ZIP_URL -OutFile $env:MOZZO_ZIP_PATH"
if errorlevel 1 goto download_error
powershell -NoProfile -ExecutionPolicy Bypass -Command "Expand-Archive -Path $env:MOZZO_ZIP_PATH -DestinationPath $env:MOZZO_INSTALL_DIR -Force"
if errorlevel 1 goto expand_error
if not exist "%LAUNCHER_PATH%" (
  echo No encontramos Mozzo.Kiosk.Windows.exe despues de instalar el paquete.
  echo Revisa tu conexion o descarga nuevamente el acceso desde MozzoApp.
  goto fail
)
> "%VERSION_FILE%" echo %PACKAGE_VERSION%

:ensure_runtime
set "MOZZO_DOTNET_ROOT=%INSTALL_DIR%\\.dotnet"
set "MOZZO_DOTNET_INSTALL=%TEMP%\\mozzo-dotnet-install.ps1"
if exist "%MOZZO_DOTNET_ROOT%\\dotnet.exe" goto run_launcher
echo Preparando runtime .NET local para impresion silenciosa...
powershell -NoProfile -ExecutionPolicy Bypass -Command "$ProgressPreference='SilentlyContinue'; Invoke-WebRequest -UseBasicParsing -Uri 'https://dot.net/v1/dotnet-install.ps1' -OutFile $env:MOZZO_DOTNET_INSTALL; & powershell -NoProfile -ExecutionPolicy Bypass -File $env:MOZZO_DOTNET_INSTALL -Runtime windowsdesktop -Channel 8.0 -InstallDir $env:MOZZO_DOTNET_ROOT -NoPath"
if errorlevel 1 (
  echo No pudimos instalar el runtime local. Intentamos abrir el launcher igualmente.
) else (
  echo Runtime .NET listo.
)

:run_launcher
if exist "%MOZZO_DOTNET_ROOT%\\dotnet.exe" (
  set "DOTNET_ROOT=%MOZZO_DOTNET_ROOT%"
  set "PATH=%MOZZO_DOTNET_ROOT%;%PATH%"
)
start "" "%LAUNCHER_PATH%" --setup --base-url="%BASE_URL%" --branch-id="%BRANCH_ID%"
endlocal
exit /b 0

:download_error
echo No pudimos descargar MozzoApp Kiosk para Windows.
echo Revisa la conexion a internet y vuelve a abrir este acceso.
goto fail

:expand_error
echo No pudimos descomprimir MozzoApp Kiosk para Windows.
echo Cierra el launcher si estaba abierto y vuelve a intentar.
goto fail

:fail
pause
endlocal
exit /b 1
`;

const ANDROID_KIOSK_APK_FILE_NAME = "MozzoKiosk-debug.apk";

const downloadTextFile = (filename, content) => {
  if (typeof window === "undefined") {
    return;
  }

  const blob = new Blob([content], {
    type: "text/plain;charset=utf-8",
  });
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
};

const createInitialCompanyProfileForm = (company = null) => ({
  ownerUserId: company?.ownerUserId || company?.owner_user_id || null,
  companyName: company?.name || "",
  legalName: company?.legalName || "",
  companyPhone: company?.phone || "",
  billingEmail: "",
  supportPhone: "",
  headquartersAddress: "",
  taxId: "",
  fiscalMode: DEFAULT_COMPANY_FISCAL_MODE,
  ownerFullName: "",
  ownerDocumentId: "",
  ownerPhone: "",
  ownerEmail: "",
  bankAccountHolder: "",
  bankName: "",
  bankAccountType: "Cuenta corriente",
  bankAccountNumber: "",
  bankCbu: "",
  bankAlias: "",
  currency: "ARS",
  timezone: "America/Argentina/Buenos_Aires",
});

const buildInternalUserForm = (branchId = "", roleKey = "admin_limitado", overrides = {}) => {
  const branchPermissions = buildMozzoRoleBranchPermissions(roleKey, overrides);

  return {
    roleKey,
    email: "",
    fullName: "",
    branchId,
    active: true,
    ...branchPermissions,
    ...overrides,
  };
};

const createInitialInviteForm = (branchId = "") =>
  buildInternalUserForm(branchId, "admin_limitado", {
    branchId,
  });

const mapPermissionsSubset = (source = {}) =>
  MOZZO_BRANCH_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = source?.[key] === true;
    return acc;
  }, {});

const isConfigurableBranchRole = (roleKey) =>
  getMozzoInternalUserRole(roleKey)?.allowsCustomBranchPermissions === true;

const shouldShowFullNameField = (roleKey) =>
  roleKey === "encargado_sucursal" ||
  roleKey === "mozo" ||
  roleKey === "cocina" ||
  roleKey === "caja" ||
  roleKey === "soporte_mozzo";

const buildInternalUserFormFromInvitation = (invitation) => {
  const roleKey = resolveMozzoInternalUserRoleKey({
    role: invitation?.role,
    workspaceRole: invitation?.workspace_role,
  });

  return buildInternalUserForm(invitation?.branch_id || "", roleKey, {
    email: invitation?.email || "",
    fullName: invitation?.display_name || "",
    active: invitation?.status !== "cancelled",
    ...mapPermissionsSubset({
      canViewMenu: invitation?.can_view_menu,
      canManageMenu: invitation?.can_manage_menu,
      canViewStock: invitation?.can_view_stock,
      canManageStock: invitation?.can_manage_stock,
      canManageOrders: invitation?.can_manage_orders,
      canManageQr: invitation?.can_manage_qr,
      canManagePayments: invitation?.can_manage_payments,
      canManageSettings: invitation?.can_manage_settings,
      waiterCanTakeOrders: invitation?.waiter_can_take_orders,
      waiterCanModifyOrders: invitation?.waiter_can_modify_orders,
      waiterCanCancelOrders: invitation?.waiter_can_cancel_orders,
      waiterCanChargeOrders: invitation?.waiter_can_charge_orders,
      waiterCanRefundOrders: invitation?.waiter_can_refund_orders,
    }),
  });
};

const buildInternalUserFormFromCompanyUser = (companyUser) => {
  const roleKey = resolveMozzoInternalUserRoleKey({
    isOwner: companyUser?.roleKey === "owner",
    role: companyUser?.role,
    workspaceRole: companyUser?.workspace_role,
  });
  const primaryBranchAccess =
    companyUser?.branchAccesses?.find((branchAccess) => branchAccess.active) ||
    companyUser?.branchAccesses?.[0] ||
    null;

  return buildInternalUserForm(companyUser?.primaryBranchId || "", roleKey, {
    email: companyUser?.email || "",
    fullName: companyUser?.profileFullName || "",
    branchId: primaryBranchAccess?.branchId || companyUser?.primaryBranchId || "",
    active: companyUser?.active !== false,
    ...mapPermissionsSubset(primaryBranchAccess?.permissions || {}),
  });
};

const getRoleModuleLabels = (roleKey, branchPermissions = {}, { isKioskBranch = false } = {}) =>
  getMozzoInternalUserRoleModuleKeys(roleKey, {
    branchPermissions,
    isKioskBranch,
  }).map(
    (moduleKey) =>
      MOZZO_MODULE_DEFINITIONS.find((moduleDefinition) => moduleDefinition.key === moduleKey)
        ?.label || moduleKey
  );

const createInitialShiftForm = (branchId = "") => ({
  userId: "",
  branchId,
  dayOfWeek: "1",
  startTime: "09:00",
  endTime: "18:00",
  notes: "",
});

const formatDay = (value) =>
  dayOptions.find((option) => option.value === Number(value))?.label || "Dia";

const formatDate = (value) => {
  if (!value) {
    return "Sin fecha";
  }

  return new Date(value).toLocaleString("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const getInvitationBranchName = (branches, branchId) =>
  !branchId
    ? "Empresa completa"
    : branches.find((branch) => branch.id === branchId)?.name || "Sucursal sin nombre";

const formatInvitationStatus = (status = "") => {
  switch (status) {
    case "pending":
      return "Pendiente";
    case "accepted":
      return "Aceptada";
    case "cancelled":
      return "Cancelada";
    default:
      return status || "Sin estado";
  }
};

const shortUserId = (value) => {
  if (!value) {
    return "Usuario";
  }

  return `${value.slice(0, 8)}...`;
};

const formatCompanyUserLabel = (companyUser, invitationEmailByUser) => {
  const fullName = companyUser?.profileFullName?.trim();
  const email =
    companyUser?.email?.trim() || invitationEmailByUser.get(companyUser?.user_id) || "";

  if (fullName && email) {
    return `${fullName} - ${email}`;
  }

  if (fullName) {
    return fullName;
  }

  if (email) {
    return email;
  }

  return shortUserId(companyUser?.user_id);
};

const getWorkspaceRoleLabel = (workspaceRole, role, isOwner = false) =>
  getMozzoInternalUserRoleLabel(
    resolveMozzoInternalUserRoleKey({
      isOwner,
      role,
      workspaceRole,
    })
  );

const buildRoleSummaryTags = (roleKey, branchPermissions, branch) => {
  if (isConfigurableBranchRole(roleKey)) {
    return MOZZO_BRANCH_PERMISSION_GROUPS
      .map((group) => {
        const canView = group.viewKey
          ? branchPermissions?.[group.viewKey] || branchPermissions?.[group.manageKey]
          : branchPermissions?.[group.manageKey];
        const canManage = branchPermissions?.[group.manageKey] === true;

        if (!canView && !canManage) {
          return "";
        }

        if (!group.viewKey) {
          return group.label;
        }

        return canManage ? `${group.label}: gestionar` : `${group.label}: lectura`;
      })
      .filter(Boolean);
  }

  const resolvedBranchProfile = branch
    ? buildResolvedBranchOperationalProfile(branch)
    : null;

  return getRoleModuleLabels(roleKey, branchPermissions, {
    isKioskBranch: resolvedBranchProfile?.serviceMode === "kiosk",
  }).slice(0, 5);
};

function Configuracion() {
  const {
    assignableInternalUserRoles,
    canAccessModule,
    canPerformAction,
    currentAccessRoleLabel,
    currentCompany,
    currentBranch,
    availableBranches,
    refreshWorkspace,
    setActiveBranch,
    user,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAccessControl();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("empresa");
  const [companyProfileForm, setCompanyProfileForm] = useState(
    createInitialCompanyProfileForm(currentCompany)
  );
  const [branchForm, setBranchForm] = useState(initialBranchForm);
  const [inviteForm, setInviteForm] = useState(
    createInitialInviteForm(currentBranch?.id || availableBranches[0]?.id || "")
  );
  const [editingInvitationId, setEditingInvitationId] = useState("");
  const [editingUserId, setEditingUserId] = useState("");
  const [shiftForm, setShiftForm] = useState(
    createInitialShiftForm(currentBranch?.id || availableBranches[0]?.id || "")
  );
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [invitations, setInvitations] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [branchMenuById, setBranchMenuById] = useState({});
  const [branchKitchenModeById, setBranchKitchenModeById] = useState({});
  const [branchKioskStationCountById, setBranchKioskStationCountById] = useState({});
  const [branchScreenPairingCodeById, setBranchScreenPairingCodeById] = useState({});
  const [androidLauncherBranchId, setAndroidLauncherBranchId] = useState("");

  const canSeeConfigurationModule = canAccessModule("configuracion");
  const canManageCompanyConfiguration =
    !isCreatorMode &&
    isSupabaseConfigured &&
    canSeeConfigurationModule &&
    canPerformAction("manage_company_settings");
  const canManageConfigurationSettings =
    !isCreatorMode &&
    isSupabaseConfigured &&
    canSeeConfigurationModule &&
    canPerformAction("manage_settings");
  const canManageConfigurationPayments =
    !isCreatorMode &&
    isSupabaseConfigured &&
    canSeeConfigurationModule &&
    canPerformAction("manage_payments");
  const canManageConfigurationUsers =
    !isCreatorMode &&
    isSupabaseConfigured &&
    canSeeConfigurationModule &&
    canPerformAction("manage_users");
  const canManageFiscalConnection =
    canManageCompanyConfiguration && canPerformAction("manage_fiscal_connection");
  const canPreviewFiscalMode = isCreatorMode && canSeeConfigurationModule;
  const canSelectFiscalMode =
    canManageCompanyConfiguration || canPreviewFiscalMode;
  const canLoadConfigurationData =
    canSeeConfigurationModule &&
    (canManageCompanyConfiguration ||
      canManageConfigurationSettings ||
      canManageConfigurationPayments ||
      canManageConfigurationUsers);
  const configurationModeMessage = isCreatorMode
    ? "Estas viendo la configuracion en modo creador. Puedes recorrer las modalidades y el asistente fiscal, pero los datos reales solo se guardan con una cuenta de dueno o admin general."
    : !isSupabaseConfigured
      ? "La pantalla ya esta visible, pero para guardar sucursales, admins limitados y turnos primero necesitamos conectar el frontend con Supabase."
      : !canManageCompanyConfiguration &&
          !canManageConfigurationSettings &&
          !canManageConfigurationPayments &&
          !canManageConfigurationUsers
        ? `${currentAccessRoleLabel} puede revisar esta pantalla, pero no modificar datos sensibles, cobros, sucursales ni accesos administrativos.`
        : !canManageCompanyConfiguration ||
            !canManageConfigurationSettings ||
            !canManageConfigurationPayments ||
            !canManageConfigurationUsers
          ? `${currentAccessRoleLabel} tiene acceso parcial: algunos bloques quedan solo de lectura segun sus permisos centrales.`
        : "";
  const configurationSettingsDisabled = saving || !canManageCompanyConfiguration;
  const branchConfigurationDisabled = saving || !canManageConfigurationSettings;
  const configurationUsersDisabled = saving || !canManageConfigurationUsers;
  const branchIds = useMemo(
    () => availableBranches.map((branch) => branch.id),
    [availableBranches]
  );
  const branchIdsKey = useMemo(() => branchIds.join("|"), [branchIds]);
  const kioskLauncherBaseUrl = useMemo(() => {
    if (typeof window === "undefined" || !window.location?.origin) {
      return "";
    }

    return window.location.origin.replace(/\/$/, "");
  }, []);
  const androidLauncherBranch = useMemo(
    () => availableBranches.find((branch) => branch.id === androidLauncherBranchId) || null,
    [androidLauncherBranchId, availableBranches]
  );
  const androidLauncherMainUrl = useMemo(() => {
    if (!androidLauncherBranch?.id) {
      return "";
    }

    return buildKioskAndroidLauncherUrl({
      baseUrl: kioskLauncherBaseUrl,
      branchId: androidLauncherBranch.id,
      setupMode: true,
    });
  }, [androidLauncherBranch, kioskLauncherBaseUrl]);
  const androidLauncherApkUrl = useMemo(() => {
    if (!mozzoKioskDebugApkUrl) {
      return "";
    }

    if (typeof window === "undefined") {
      return mozzoKioskDebugApkUrl;
    }

    return new URL(mozzoKioskDebugApkUrl, window.location.origin).toString();
  }, []);
  const androidLauncherStationLinks = useMemo(() => {
    if (!androidLauncherBranch?.id) {
      return [];
    }

    const branchProfile = buildResolvedBranchOperationalProfile(androidLauncherBranch);
    const stationCount =
      branchKioskStationCountById[androidLauncherBranch.id] ??
      branchProfile.kioskStationCount;

    return Array.from({ length: stationCount }, (_, index) => {
      const stationNumber = index + 1;

      return {
        stationNumber,
        url: buildKioskAndroidLauncherUrl({
          baseUrl: kioskLauncherBaseUrl,
          branchId: androidLauncherBranch.id,
          stationNumber,
        }),
      };
    }).filter((item) => item.url);
  }, [androidLauncherBranch, branchKioskStationCountById, kioskLauncherBaseUrl]);

  useEffect(() => {
    setBranchKitchenModeById(
      availableBranches.reduce((acc, branch) => {
        const branchProfile = buildResolvedBranchOperationalProfile(branch);
        acc[branch.id] = branchProfile.kitchenMode || "manual";
        return acc;
      }, {})
    );
    setBranchKioskStationCountById(
      availableBranches.reduce((acc, branch) => {
        acc[branch.id] = buildResolvedBranchOperationalProfile(branch).kioskStationCount;
        return acc;
      }, {})
    );
  }, [availableBranches]);

  useEffect(() => {
    const defaultBranchId = currentBranch?.id || availableBranches[0]?.id || "";

    setInviteForm((prev) =>
      prev.branchId ? prev : createInitialInviteForm(defaultBranchId)
    );
    setShiftForm((prev) =>
      prev.branchId ? prev : createInitialShiftForm(defaultBranchId)
    );
  }, [availableBranches, currentBranch?.id]);

  useEffect(() => {
    if (!canLoadConfigurationData) {
      setCompanyProfileForm(createInitialCompanyProfileForm(currentCompany));
    }
  }, [canLoadConfigurationData, currentCompany]);

  const notifyConfigurationPermissionDenied = useCallback(
    (actionLabel, scope = "settings") => {
      setSuccessMessage("");

      const message =
        scope === "payments"
          ? `${currentAccessRoleLabel} puede revisar cobros, pero no ${actionLabel} en esta sucursal.`
          : scope === "users"
            ? `${currentAccessRoleLabel} puede consultar usuarios internos, pero no ${actionLabel} en esta empresa.`
            : `${currentAccessRoleLabel} puede revisar la configuracion general, pero no ${actionLabel} desde esta sesion.`;

      setErrorMessage(message);
      return false;
    },
    [currentAccessRoleLabel]
  );

  const ensureConfigurationSettingsAccess = useCallback(
    (actionLabel) => {
      if (canManageConfigurationSettings) {
        return true;
      }

      return notifyConfigurationPermissionDenied(actionLabel, "settings");
    },
    [canManageConfigurationSettings, notifyConfigurationPermissionDenied]
  );

  const ensureCompanyConfigurationAccess = useCallback(
    (actionLabel) => {
      if (canManageCompanyConfiguration) {
        return true;
      }

      setSuccessMessage("");
      setErrorMessage(
        `${currentAccessRoleLabel} puede administrar su sucursal, pero no ${actionLabel} a nivel empresa.`
      );
      return false;
    }, [canManageCompanyConfiguration, currentAccessRoleLabel]);

  const ensureConfigurationUsersAccess = useCallback(
    (actionLabel) => {
      if (canManageConfigurationUsers) {
        return true;
      }

      return notifyConfigurationPermissionDenied(actionLabel, "users");
    },
    [canManageConfigurationUsers, notifyConfigurationPermissionDenied]
  );

  const loadAdminData = useCallback(async () => {
    if (!currentCompany?.id || !canLoadConfigurationData) {
      return;
    }

    setLoadingData(true);

    try {
      const [
        nextCompanyProfile,
        nextInvitations,
        nextUsers,
        nextShifts,
        nextBranchMenus,
        nextScreenPairingCodes,
      ] =
        await Promise.all([
          getCompanyProfile(currentCompany.id),
          listCompanyInvitations(currentCompany.id),
          listCompanyUsers(currentCompany.id, { includeInactive: true }),
          listAdminShifts(branchIds),
          getBranchesMenuSetup(branchIds),
          canManageConfigurationSettings
            ? getBranchScreenPairingCodes(branchIds)
            : Promise.resolve({}),
        ]);

      setCompanyProfileForm(nextCompanyProfile);
      setInvitations(nextInvitations);
      setCompanyUsers(nextUsers);
      setShifts(nextShifts);
      setBranchMenuById(
        nextBranchMenus.reduce((acc, setup) => {
          acc[setup.branchId] = setup;
          return acc;
        }, {})
      );
      setBranchScreenPairingCodeById(nextScreenPairingCodes);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "No se pudieron cargar los datos de configuracion.");
    } finally {
      setLoadingData(false);
    }
  }, [
    branchIds,
    canLoadConfigurationData,
    canManageConfigurationSettings,
    currentCompany?.id,
  ]);

  const refreshBranchMenus = useCallback(async () => {
    if (!branchIds.length || !canLoadConfigurationData) {
      return;
    }

    const nextBranchMenus = await getBranchesMenuSetup(branchIds);
    setBranchMenuById(
      nextBranchMenus.reduce((acc, setup) => {
        acc[setup.branchId] = setup;
        return acc;
      }, {})
    );
  }, [branchIds, canLoadConfigurationData]);

  useEffect(() => {
    loadAdminData();
  }, [branchIdsKey, loadAdminData]);

  const invitationEmailByUser = useMemo(() => {
    const map = new Map();

    invitations.forEach((invitation) => {
      if (invitation.accepted_by && invitation.email) {
        map.set(invitation.accepted_by, invitation.email);
      }
    });

    if (user?.id && user?.email) {
      map.set(user.id, user.email);
    }

    return map;
  }, [invitations, user?.email, user?.id]);
  const ownerUserId = companyProfileForm.ownerUserId || null;
  const isProtectedOwnerUser = useCallback(
    (companyUser) => {
      if (!companyUser) {
        return false;
      }

      return ownerUserId && companyUser.user_id === ownerUserId;
    },
    [ownerUserId]
  );
  const isProtectedOwnerInvitation = useCallback(
    (invitation) => {
      if (!invitation) {
        return false;
      }

      const invitationEmail = invitation.email?.trim().toLowerCase() || "";
      const currentUserEmail = user?.email?.trim().toLowerCase() || "";

      return (
        (ownerUserId && invitation.accepted_by === ownerUserId) ||
        (currentCompany?.role === "admin_general" &&
          Boolean(currentUserEmail) &&
          invitationEmail === currentUserEmail)
      );
    },
    [currentCompany?.role, ownerUserId, user?.email]
  );

  const limitedAdmins = useMemo(
    () =>
      companyUsers.filter(
        (companyUser) =>
          !isProtectedOwnerUser(companyUser) &&
          companyUser.active !== false &&
          resolveMozzoInternalUserRoleKey({
            role: companyUser.role,
            workspaceRole: companyUser.workspace_role,
          }) === "admin_limitado"
      ),
    [companyUsers, isProtectedOwnerUser]
  );
  const manageableTeamUsers = useMemo(
    () => companyUsers.filter((companyUser) => !isProtectedOwnerUser(companyUser)),
    [companyUsers, isProtectedOwnerUser]
  );
  const selectedInviteRole = useMemo(
    () => getMozzoInternalUserRole(inviteForm.roleKey),
    [inviteForm.roleKey]
  );
  const selectedInviteBranch = useMemo(
    () => availableBranches.find((branch) => branch.id === inviteForm.branchId) || null,
    [availableBranches, inviteForm.branchId]
  );
  const selectedInviteRoleModules = useMemo(
    () => {
      const selectedInviteBranchProfile = selectedInviteBranch
        ? buildResolvedBranchOperationalProfile(selectedInviteBranch)
        : null;

      return getRoleModuleLabels(inviteForm.roleKey, mapPermissionsSubset(inviteForm), {
        isKioskBranch: selectedInviteBranchProfile?.serviceMode === "kiosk",
      });
    },
    [inviteForm, selectedInviteBranch]
  );
  const internalUserRoleOptions = useMemo(
    () =>
      assignableInternalUserRoles.filter((roleDefinition) => roleDefinition.key !== "owner"),
    [assignableInternalUserRoles]
  );
  const isEditingInternalUser = Boolean(editingInvitationId || editingUserId);
  const editingModeLabel = editingInvitationId
    ? "Editar invitacion"
    : editingUserId
      ? "Editar usuario"
      : "Crear usuario";

  const resetInternalUserForm = useCallback(
    (branchId = currentBranch?.id || availableBranches[0]?.id || "") => {
      setInviteForm(createInitialInviteForm(branchId));
      setEditingInvitationId("");
      setEditingUserId("");
    },
    [availableBranches, currentBranch?.id]
  );

  const applyInternalUserRoleSelection = useCallback((nextRoleKey) => {
    setInviteForm((prev) =>
      buildInternalUserForm(prev.branchId, nextRoleKey, {
        email: prev.email,
        fullName: prev.fullName,
        branchId: prev.branchId,
        active: prev.active,
      })
    );
  }, []);

  const updateInvitePermission = (group, field, checked) => {
    setInviteForm((prev) =>
      updateMozzoBranchPermissionGroup(prev, group, field, checked)
    );
  };

  const handleSaveCompanyProfile = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureCompanyConfigurationAccess("editar los datos fiscales y administrativos")) {
      return;
    }

    if (!companyProfileForm.companyName.trim()) {
      setErrorMessage("El nombre de la empresa es obligatorio.");
      return;
    }

    if (!companyProfileForm.ownerFullName.trim()) {
      setErrorMessage("El nombre del titular es obligatorio.");
      return;
    }

    if (!companyProfileForm.ownerEmail.trim()) {
      setErrorMessage("El email del titular es obligatorio.");
      return;
    }

    if (!companyProfileForm.bankCbu.trim() && !companyProfileForm.bankAlias.trim()) {
      setErrorMessage("Carga al menos un CBU o alias para poder depositar las ganancias.");
      return;
    }

    setSaving(true);

    try {
      const updatedProfile = await updateCompanyProfile(
        currentCompany.id,
        companyProfileForm
      );
      setCompanyProfileForm(updatedProfile);
      await refreshWorkspace(user, currentBranch?.id || availableBranches[0]?.id || "");
      setSuccessMessage("Datos de empresa y modalidad fiscal actualizados.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar la informacion de la empresa.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBranch = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureCompanyConfigurationAccess("crear sucursales")) {
      return;
    }

    if (!branchForm.name.trim()) {
      setErrorMessage("El nombre de la sucursal es obligatorio.");
      return;
    }

    if (!branchForm.serviceMode) {
      setErrorMessage("Necesitas elegir si la sucursal sera restaurante o kiosk.");
      return;
    }

    const moduleValidationError = getBranchOperationalModulesError(branchForm);
    if (moduleValidationError) {
      setErrorMessage(moduleValidationError);
      return;
    }

    setSaving(true);

    try {
      const created = await createBranch(currentCompany.id, branchForm);
      saveRestaurantProfile(created, {
        ...getRestaurantProfile(created),
        kitchenMode: branchForm.serviceMode === "kiosk" ? "screen" : "manual",
        serviceMode: branchForm.serviceMode === "kiosk" ? "kiosk" : "salon",
        salonEnabled: created.salonEnabled,
        deliveryEnabled: created.deliveryEnabled,
        operationalSetupCompleted: true,
        waiterEnabled: created.waiterEnabled,
        stockEnabled: created.stockEnabled,
        aiEnabled: created.aiEnabled,
        wePedidosEnabled: created.wePedidosEnabled,
        kioskMenuMode:
          branchForm.serviceMode === "kiosk" && branchForm.kioskMenuMode === "custom"
            ? "custom"
            : "current",
        kioskStationCount: normalizeKioskStationCount(branchForm.kioskStationCount),
      });
      await refreshWorkspace(user, created.id);
      setActiveBranch(created.id);
      setBranchForm(initialBranchForm);
      setSuccessMessage(
        `Sucursal creada correctamente como ${
          branchForm.serviceMode === "kiosk" ? "kiosk" : "restaurante"
        }.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo crear la sucursal.");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignSharedMenu = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("reasignar el menu compartido")) {
      return;
    }

    setSaving(true);

    try {
      await assignSharedMenuToBranch(branch.id);
      await refreshBranchMenus();
      setSuccessMessage(`La sucursal ${branch.name} ahora usa el menu compartido.`);
    } catch (error) {
      setErrorMessage(error.message || "No se pudo cambiar la asignacion del menu.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCustomMenu = async (branch, createEmpty = false) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("crear o personalizar menus de sucursal")) {
      return;
    }

    setSaving(true);

    try {
      await createCustomMenuForBranch(branch.id, {
        copyFromCurrent: !createEmpty,
        createEmpty,
      });
      await refreshBranchMenus();
      setSuccessMessage(
        createEmpty
          ? `La sucursal ${branch.name} ya tiene un menu propio base.`
          : `La sucursal ${branch.name} ahora tiene una copia propia del menu.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo crear el menu propio.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranch = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureCompanyConfigurationAccess("eliminar sucursales")) {
      return;
    }

    if (availableBranches.length <= 1) {
      setErrorMessage("No puedes eliminar la unica sucursal activa de la empresa.");
      return;
    }

    const typedName = window.prompt(
      `Para eliminar la sucursal "${branch.name}", escribe exactamente su nombre.`
    );

    if (typedName === null) {
      return;
    }

    if (typedName.trim() !== branch.name) {
      setErrorMessage("El nombre no coincide. La sucursal no fue eliminada.");
      return;
    }

    setSaving(true);

    try {
      const remainingBranches = availableBranches.filter((item) => item.id !== branch.id);
      const nextBranchId =
        currentBranch?.id === branch.id
          ? remainingBranches[0]?.id || ""
          : currentBranch?.id || remainingBranches[0]?.id || "";

      await deleteBranch(currentCompany.id, branch.id);
      await refreshWorkspace(user, nextBranchId);

      if (nextBranchId) {
        setActiveBranch(nextBranchId);
      }

      resetInternalUserForm(nextBranchId);
      setShiftForm(createInitialShiftForm(nextBranchId));
      setSuccessMessage(
        `Sucursal eliminada. Mas adelante podemos reemplazar esta confirmacion por un codigo enviado al correo.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo eliminar la sucursal.");
    } finally {
      setSaving(false);
    }
  };

  const handleBranchKitchenModeChange = async (branch, nextMode) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("cambiar la operacion de cocina")) {
      return;
    }

    const currentProfile = buildResolvedBranchOperationalProfile(
      branch,
      getRestaurantProfile(branch)
    );
    const normalizedServiceMode = currentProfile.serviceMode || "salon";

    if (normalizedServiceMode === "kiosk" && !KIOSK_KITCHEN_MODE_IDS.has(nextMode)) {
      setErrorMessage(
        "Las sucursales kiosk solo pueden trabajar con cocina por pantalla o impresora."
      );
      return;
    }

    const resolvedMode = normalizeKitchenModeForServiceMode(normalizedServiceMode, nextMode);

    try {
      const nextProfile = saveRestaurantProfile(branch, {
        ...currentProfile,
        kitchenMode: resolvedMode,
      });

      try {
        await updateBranchKitchenMode(branch.id, resolvedMode);
      } catch (syncError) {
        setSuccessMessage(
          `Guardamos el modo ${resolvedMode} en este equipo, pero todavia no se pudo sincronizar con Supabase.`
        );
      }

      setBranchKitchenModeById((prev) => ({
        ...prev,
        [branch.id]: normalizeKitchenModeForServiceMode(
          normalizedServiceMode,
          nextProfile.kitchenMode || resolvedMode
        ),
      }));

      const nextModeLabel =
        KITCHEN_MODE_OPTIONS.find((option) => option.id === resolvedMode)?.label ||
        "Pantalla";
      setSuccessMessage(
        `La sucursal ${branch.name} ahora opera cocina en modo ${nextModeLabel.toLowerCase()}.`
      );
    } catch (error) {
      setErrorMessage(
        error.message || "No se pudo guardar la operacion de cocina de la sucursal."
      );
    }
  };

  const handleBranchKioskStationCountChange = async (branch, nextCount) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("modificar la cantidad de totems")) {
      return;
    }

    const normalizedCount = normalizeKioskStationCount(nextCount);
    const currentProfile = buildResolvedBranchOperationalProfile(
      branch,
      getRestaurantProfile(branch)
    );
    const currentCount =
      branchKioskStationCountById[branch.id] ??
      currentProfile.kioskStationCount;
    const currentOptionId = resolveKioskOptionId(currentProfile);
    const currentOption =
      KIOSK_EXPERIENCE_OPTIONS.find((option) => option.id === currentOptionId) ||
      KIOSK_EXPERIENCE_OPTIONS[0];

    if (normalizedCount === currentCount) {
      if (Number(nextCount) < 1) {
        setErrorMessage(
          "Por ahora el minimo para una sucursal kiosk es 1 totem activo. Mas adelante agregamos la baja total con confirmacion y contrasena."
        );
      }
      return;
    }

    try {
      let trimmedClaimsCount = 0;

      if (normalizedCount < currentCount && branch?.id) {
        try {
          const sharedState = await fetchSharedOperationalState(
            { id: branch.id },
            EMPTY_OPERATIONAL_STATE
          );
          const activeClaims = sharedState.supported
            ? getActiveKioskTerminalClaims(sharedState.payload)
            : [];
          const affectedClaims = activeClaims.filter(
            (claim) => claim.stationNumber > normalizedCount
          );

          if (affectedClaims.length > 0) {
            const affectedStationsLabel = affectedClaims
              .map((claim) => `Totem ${claim.stationNumber}`)
              .join(", ");
            const confirmed = window.confirm(
              `Estas por dejar fuera de servicio ${affectedStationsLabel}, y ahora figuran en uso. Si continuas, esas terminales quedaran desactivadas para la sucursal. Mas adelante vamos a proteger esta accion con contrasena. Deseas seguir igual?`
            );

            if (!confirmed) {
              return;
            }

            trimmedClaimsCount = affectedClaims.length;
            await pushSharedOperationalState(
              { id: branch.id },
              {
                kioskTerminalClaims: activeClaims.filter(
                  (claim) => claim.stationNumber <= normalizedCount
                ),
              },
              EMPTY_OPERATIONAL_STATE
            );
          }
        } catch (claimError) {
          setErrorMessage(
            claimError.message ||
              "No pudimos validar si habia totems en uso antes de reducir la cantidad."
          );
          return;
        }
      }

      saveRestaurantProfile(branch, {
        ...currentProfile,
        kioskStationCount: normalizedCount,
        serviceMode: currentOption.serviceMode,
        kioskMenuMode: currentOption.kioskMenuMode,
      });
      setBranchKioskStationCountById((prev) => ({
        ...prev,
        [branch.id]: normalizedCount,
      }));

      try {
        await updateBranchKioskConfig(branch.id, {
          serviceMode: currentOption.serviceMode,
          kioskMenuMode: currentOption.kioskMenuMode,
          kioskStationCount: normalizedCount,
        });
      } catch (syncError) {
        setSuccessMessage(
          `Guardamos la cantidad de totems en este equipo, pero todavia no se pudo sincronizar con Supabase.`
        );
      }

      setSuccessMessage(
        `La sucursal ${branch.name} ahora queda preparada para ${normalizedCount} totem${
          normalizedCount === 1 ? "" : "s"
        }${
          trimmedClaimsCount > 0
            ? `. Tambien desactivamos ${trimmedClaimsCount} totem${
                trimmedClaimsCount === 1 ? "" : "s"
              } que figuraban en uso por encima de ese limite.`
            : "."
        }`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar la cantidad de totems.");
    }
  };

  const handleOpenKioskStation = (branch, stationNumber) => {
    if (!ensureConfigurationSettingsAccess("abrir puestos de kiosk")) {
      return;
    }

    setActiveBranch(branch.id);
    navigate(`/totem/${branch.id}/${normalizeKioskStationCount(stationNumber)}`);
  };

  const handleDownloadKioskWindowsLauncher = (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("descargar el acceso Windows del kiosk")) {
      return;
    }

    const baseUrl =
      typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "";

    if (!baseUrl) {
      setErrorMessage("No pudimos detectar la URL base para generar el launcher del kiosk.");
      return;
    }

    downloadTextFile(
      buildKioskLauncherFileName(branch.name),
      buildKioskWindowsLauncherContent({
        baseUrl,
        branchId: branch.id,
      })
    );

    setSuccessMessage(
      `Descargamos el acceso Windows de ${branch.name}. Al abrirlo por primera vez prepara MozzoApp Kiosk WebView2 y despues permite elegir impresora, ancho de ticket y totem de esta terminal.`
    );
  };

  const handleOpenKioskAndroidLauncher = (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("preparar el launcher Android del kiosk")) {
      return;
    }

    if (!branch?.id) {
      setErrorMessage("No encontramos la sucursal para preparar el launcher Android.");
      return;
    }

    if (!kioskLauncherBaseUrl) {
      setErrorMessage("No pudimos detectar la URL base para generar el launcher Android.");
      return;
    }

    setAndroidLauncherBranchId(branch.id);
  };

  const handleCloseKioskAndroidLauncher = () => {
    setAndroidLauncherBranchId("");
  };

  const handleOpenAndroidLauncherLink = (url) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("abrir el launcher Android")) {
      return;
    }

    if (!url) {
      setErrorMessage("No pudimos generar el link del launcher Android.");
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadAndroidKioskApk = () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("descargar la APK del kiosk")) {
      return;
    }

    if (typeof window === "undefined" || typeof document === "undefined") {
      setErrorMessage("No pudimos abrir la descarga de la APK desde este equipo.");
      return;
    }

    if (!androidLauncherApkUrl) {
      setErrorMessage("Todavia no encontramos la APK compilada de Mozzo Kiosk.");
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = androidLauncherApkUrl;
    anchor.download = ANDROID_KIOSK_APK_FILE_NAME;
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setSuccessMessage(
      "Abrimos la descarga de Mozzo Kiosk. Si la tablet lo pide, autoriza instalar APK desde ese navegador."
    );
  };

  const handleCopyAndroidApkLink = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("copiar el link de la APK del kiosk")) {
      return;
    }

    try {
      if (!androidLauncherApkUrl) {
        throw new Error("Todavia no encontramos la APK compilada de Mozzo Kiosk.");
      }

      await copyTextToClipboard(androidLauncherApkUrl);
      setSuccessMessage("Copiamos el link directo de descarga de la APK.");
    } catch (error) {
      setErrorMessage(error.message || "No pudimos copiar el link de la APK.");
    }
  };

  const handleCopyAndroidLauncherLink = async (url, label) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("copiar links del launcher Android")) {
      return;
    }

    try {
      if (!url) {
        throw new Error("No pudimos generar el link del launcher Android.");
      }

      await copyTextToClipboard(url);
      setSuccessMessage(`Copiamos el link ${label} para abrirlo desde Android.`);
    } catch (error) {
      setErrorMessage(error.message || "No pudimos copiar el link del launcher Android.");
    }
  };

  const handleOpenPublicDisplay = (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("abrir la pantalla publica de preparacion")) {
      return;
    }

    const pairingCode = branchScreenPairingCodeById[branch?.id] || "";
    const displayUrl = buildPublicDisplayUrl(pairingCode);

    if (!pairingCode || !displayUrl) {
      setErrorMessage("No pudimos generar el codigo de vinculacion para esta pantalla.");
      return;
    }

    window.open(displayUrl, "_blank", "noopener,noreferrer");
    setSuccessMessage(
      `Abrimos la pantalla publica de ${branch.name}. Ese enlace ya queda vinculado a la sucursal para mostrar preparacion y listo para retirar.`
    );
  };

  const handleCopyPublicDisplayCode = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("copiar el codigo del display publico")) {
      return;
    }

    try {
      const pairingCode = branchScreenPairingCodeById[branch?.id] || "";

      if (!pairingCode) {
        throw new Error("No pudimos generar el codigo de vinculacion de esta sucursal.");
      }

      await copyTextToClipboard(pairingCode);
      setSuccessMessage(
        `Copiamos el codigo ${pairingCode} para vincular la pantalla de ${branch.name}.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo copiar el codigo de la pantalla.");
    }
  };

  const handleCopyPublicDisplayLink = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("copiar el link del display publico")) {
      return;
    }

    try {
      const pairingCode = branchScreenPairingCodeById[branch?.id] || "";
      const displayUrl = buildPublicDisplayUrl(pairingCode);

      if (!pairingCode || !displayUrl) {
        throw new Error("No pudimos generar el link publico de esta pantalla.");
      }

      await copyTextToClipboard(displayUrl);
      setSuccessMessage(
        `Copiamos el link publico del display de ${branch.name}.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo copiar el link del display.");
    }
  };

  const handleOpenPublicKitchen = (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("abrir la cocina publica")) {
      return;
    }

    const pairingCode = branchScreenPairingCodeById[branch?.id] || "";
    const kitchenUrl = buildPublicKitchenUrl(pairingCode);

    if (!pairingCode || !kitchenUrl) {
      setErrorMessage("No pudimos generar el codigo de vinculacion para esta cocina.");
      return;
    }

    window.open(kitchenUrl, "_blank", "noopener,noreferrer");
    setSuccessMessage(
      `Abrimos la cocina publica de ${branch.name}. Ese enlace ya queda listo para tomar pedidos con codigo desde otra pantalla.`
    );
  };

  const handleCopyPublicKitchenCode = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("copiar el codigo de cocina")) {
      return;
    }

    try {
      const pairingCode = branchScreenPairingCodeById[branch?.id] || "";

      if (!pairingCode) {
        throw new Error("No pudimos generar el codigo de vinculacion de esta cocina.");
      }

      await copyTextToClipboard(pairingCode);
      setSuccessMessage(
        `Copiamos el codigo ${pairingCode} para vincular la cocina de ${branch.name}.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo copiar el codigo de cocina.");
    }
  };

  const handleCopyPublicKitchenLink = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("copiar el link publico de cocina")) {
      return;
    }

    try {
      const pairingCode = branchScreenPairingCodeById[branch?.id] || "";
      const kitchenUrl = buildPublicKitchenUrl(pairingCode);

      if (!pairingCode || !kitchenUrl) {
        throw new Error("No pudimos generar el link publico de esta cocina.");
      }

      await copyTextToClipboard(kitchenUrl);
      setSuccessMessage(
        `Copiamos el link publico de cocina de ${branch.name}.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo copiar el link de cocina.");
    }
  };

  const handleRotatePublicScreenCode = async (branch) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationSettingsAccess("regenerar el codigo de Cocina y Display")) {
      return;
    }

    const confirmed = window.confirm(
      "El codigo actual dejara de funcionar en Cocina y Display. Tendras que ingresar el nuevo codigo en esos dispositivos. ¿Quieres continuar?"
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);

    try {
      const pairingCode = await rotateBranchScreenPairingCode(branch?.id);
      setBranchScreenPairingCodeById((currentCodes) => ({
        ...currentCodes,
        [branch.id]: pairingCode,
      }));
      setSuccessMessage(
        `Regeneramos el codigo seguro de ${branch.name}. Las pantallas anteriores quedaron desvinculadas.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No pudimos regenerar el codigo de pantalla.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvitation = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("crear o editar usuarios internos")) {
      return;
    }

    setSaving(true);

    try {
      if (shouldShowFullNameField(inviteForm.roleKey) && !inviteForm.fullName.trim()) {
        throw new Error("Necesitas cargar un nombre visible para este usuario operativo.");
      }

      const payload = {
        ...inviteForm,
        branchId: selectedInviteRole?.scope === "branch" ? inviteForm.branchId : "",
      };
      const roleLabel = getMozzoInternalUserRoleLabel(inviteForm.roleKey);
      if (editingInvitationId) {
        const updated = await updateInternalUserInvitation(
          currentCompany.id,
          editingInvitationId,
          payload
        );
        setInvitations((prev) =>
          prev.map((invitation) =>
            invitation.id === editingInvitationId ? updated : invitation
          )
        );
        resetInternalUserForm(inviteForm.branchId);
        setSuccessMessage(`Actualizamos la invitacion de ${roleLabel.toLowerCase()}.`);
        return;
      }

      if (editingUserId) {
        await updateCompanyUserAccess(currentCompany.id, editingUserId, payload);
        await loadAdminData();
        resetInternalUserForm(inviteForm.branchId);
        setSuccessMessage(`Actualizamos el acceso de ${roleLabel.toLowerCase()}.`);
        return;
      }

      const created = await createInternalUserInvitation(currentCompany.id, user?.id, payload);
      setInvitations((prev) => [created, ...prev]);
      resetInternalUserForm(inviteForm.branchId);
      if (created.invitation_email_sent === false) {
        setErrorMessage(
          created.invitation_email_error ||
            "La invitacion quedo creada, pero no se pudo enviar el correo de acceso."
        );
      } else {
        setSuccessMessage(`Invitacion de ${roleLabel.toLowerCase()} creada correctamente.`);
      }
    } catch (error) {
      setErrorMessage(error.message || "No se pudo crear la invitacion.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditInvitation = useCallback((invitation) => {
    if (!ensureConfigurationUsersAccess("editar invitaciones internas")) {
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setEditingUserId("");
    setEditingInvitationId(invitation?.id || "");
    setInviteForm(buildInternalUserFormFromInvitation(invitation));
  }, [ensureConfigurationUsersAccess]);

  const handleEditCompanyUser = useCallback((companyUser) => {
    if (!ensureConfigurationUsersAccess("editar usuarios internos")) {
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setEditingInvitationId("");
    setEditingUserId(companyUser?.user_id || "");
    const nextForm = buildInternalUserFormFromCompanyUser(companyUser);
    nextForm.email =
      companyUser?.email?.trim() || invitationEmailByUser.get(companyUser?.user_id) || "";
    setInviteForm(nextForm);
  }, [ensureConfigurationUsersAccess, invitationEmailByUser]);

  const handleRestoreCompanyUser = useCallback(
    async (companyUser) => {
      if (!ensureConfigurationUsersAccess("reactivar usuarios internos")) {
        return;
      }

      if (!currentCompany?.id) {
        setErrorMessage("No encontramos la empresa activa para reactivar el usuario.");
        return;
      }

      const baseForm = buildInternalUserFormFromCompanyUser(companyUser);

      if (!baseForm.branchId && getMozzoInternalUserRole(baseForm.roleKey)?.scope === "branch") {
        setErrorMessage(
          "Ese usuario no tiene sucursal asignada para volver a activarlo. Editalo y define una sucursal primero."
        );
        return;
      }

      setSaving(true);

      try {
        await updateCompanyUserAccess(currentCompany.id, companyUser.user_id, {
          ...baseForm,
          active: true,
        });
        await loadAdminData();
        setSuccessMessage("Usuario reactivado correctamente.");
      } catch (error) {
        setErrorMessage(error.message || "No se pudo reactivar el usuario.");
      } finally {
        setSaving(false);
      }
    },
    [currentCompany?.id, ensureConfigurationUsersAccess, loadAdminData]
  );

  const handleCancelInvitation = async (invitationId) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("cancelar invitaciones")) {
      return;
    }

    setSaving(true);

    try {
      const updated = await cancelInvitation(currentCompany.id, invitationId);
      setInvitations((prev) =>
        prev.map((invitation) =>
          invitation.id === invitationId ? updated : invitation
        )
      );
      setSuccessMessage("Invitacion cancelada.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo cancelar la invitacion.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("eliminar invitaciones")) {
      return;
    }

    if (!currentCompany?.id) {
      setErrorMessage("No encontramos la empresa activa para eliminar la invitacion.");
      return;
    }

    setSaving(true);

    try {
      await deleteInvitation(currentCompany.id, invitationId);
      setInvitations((prev) => prev.filter((invitation) => invitation.id !== invitationId));
      setSuccessMessage("Invitacion eliminada del listado.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo eliminar la invitacion.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyInvitationLink = async (invitation) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("copiar links de invitacion")) {
      return;
    }

    try {
      const registrationUrl = buildInvitationRegistrationUrl(invitation);
      await copyTextToClipboard(registrationUrl);
      setSuccessMessage("Link de registro copiado. El usuario ya puede crear su cuenta desde ahi.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo copiar el link de registro.");
    }
  };

  const handleRemoveCompanyUser = async (companyUser) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("desactivar usuarios internos")) {
      return;
    }

    if (!currentCompany?.id) {
      setErrorMessage("No encontramos la empresa activa para eliminar el usuario.");
      return;
    }

    if (isProtectedOwnerUser(companyUser)) {
      setErrorMessage("El admin general titular de la empresa no se puede eliminar.");
      return;
    }

    const workspaceRole = companyUser.workspace_role || companyUser.role;
    const confirmed = window.confirm(
      `Vas a desactivar el acceso de ${getWorkspaceRoleLabel(workspaceRole, companyUser.role).toLowerCase()} para ${formatCompanyUserLabel(
        companyUser,
        invitationEmailByUser
      )}. El usuario dejara de operar en la empresa hasta volver a activarlo.`
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);

    try {
      await deactivateCompanyUser(currentCompany.id, companyUser.user_id);
      await loadAdminData();
      setSuccessMessage(
        `${getWorkspaceRoleLabel(workspaceRole, companyUser.role)} desactivado correctamente.`
      );
    } catch (error) {
      setErrorMessage(error.message || "No se pudo eliminar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateShift = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("crear turnos")) {
      return;
    }

    setSaving(true);

    try {
      const created = await createAdminShift(shiftForm);
      setShifts((prev) => [created, ...prev]);
      setShiftForm(createInitialShiftForm(shiftForm.branchId));
      setSuccessMessage("Turno creado correctamente.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo crear el turno.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!ensureConfigurationUsersAccess("eliminar turnos")) {
      return;
    }

    setSaving(true);

    try {
      await deleteAdminShift(shiftId);
      setShifts((prev) => prev.filter((shift) => shift.id !== shiftId));
      setSuccessMessage("Turno eliminado.");
    } catch (error) {
      setErrorMessage(error.message || "No se pudo eliminar el turno.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerBlock}>
        <div style={styles.eyebrow}>Empresa y accesos</div>
        <h1 style={styles.title}>Configuracion general</h1>
        <p style={styles.subtitle}>
          Administra sucursales, invita admins limitados y registra turnos para la
          empresa <strong>{currentCompany?.name || "activa"}</strong>.
        </p>
      </div>

      {configurationModeMessage && (
        <div style={styles.banner}>
          {configurationModeMessage}
        </div>
      )}

      <div style={styles.sectionSwitch}>
        {[
          { id: "empresa", label: "Empresa y facturacion" },
          { id: "sucursales", label: "Sucursales" },
          { id: "admins", label: "Usuarios y accesos" },
          { id: "turnos", label: "Turnos" },
          ...(!isCreatorMode && ownerUserId === user?.id ? [{ id: "contrato", label: "Mi contrato" }] : []),
          ...(!isCreatorMode && ownerUserId === user?.id ? [{ id: "baja", label: "Baja y datos" }] : []),
        ].map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            style={{
              ...styles.sectionButton,
              ...(activeSection === section.id ? styles.sectionButtonActive : {}),
            }}
          >
            {section.label}
          </button>
        ))}
      </div>

      {(errorMessage || successMessage) && (
        <div
          style={{
            ...styles.banner,
            ...(errorMessage ? styles.bannerError : styles.bannerSuccess),
          }}
        >
          {errorMessage || successMessage}
        </div>
      )}

      {loadingData && (
        <div style={styles.banner}>
          Cargando configuracion de sucursales, accesos y turnos...
        </div>
      )}

      {activeSection === "contrato" && !isCreatorMode && ownerUserId === user?.id && (
        <RestaurantContractReceipts companyId={currentCompany?.id} />
      )}
      {activeSection === "baja" && !isCreatorMode && ownerUserId === user?.id && (
        <RestaurantLifecyclePanel companyId={currentCompany.id}
          onChanged={() => refreshWorkspace(user, currentBranch?.id || "")} />
      )}
      {activeSection === "empresa" && (
        <div style={styles.sectionBand}>
          <div style={styles.sectionIntro}>
            <h2 style={styles.sectionTitle}>Empresa, facturacion y cobros</h2>
            <p style={styles.sectionDescription}>
              Estos datos pertenecen a la empresa madre. Las sucursales pueden tener telefono,
              domicilio y descripcion propios, pero el titular y la cuenta bancaria desde donde
              se liquidan las ganancias se administran aqui.
            </p>
          </div>

          {!canManageCompanyConfiguration ? (
            <div style={styles.banner}>
              {isCreatorMode
                ? "Vista de demostracion: puedes abrir Facturacion ARCA y recorrer el asistente. Para vincular un CUIT y guardar datos reales, ingresa con la cuenta del restaurante."
                : `${currentAccessRoleLabel} puede revisar los datos fiscales y administrativos, pero no modificarlos desde esta sesion.`}
            </div>
          ) : null}

          <form onSubmit={handleSaveCompanyProfile} style={styles.formGrid}>
            <fieldset style={styles.fiscalModePanel}>
              <legend style={styles.fiscalModeLegend}>Modalidad fiscal</legend>
              <p style={styles.fiscalModeLead}>
                La empresa elige como gestiona sus comprobantes. Esta preferencia se
                aplica a todas sus sucursales y no depende del medio de pago.
              </p>

              <div style={styles.fiscalModeGrid} role="radiogroup" aria-label="Modalidad fiscal">
                {COMPANY_FISCAL_MODE_OPTIONS.map((option) => {
                  const isSelected = companyProfileForm.fiscalMode === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() =>
                        setCompanyProfileForm((prev) => ({
                          ...prev,
                          fiscalMode: option.value,
                        }))
                      }
                      style={{
                        ...styles.fiscalModeCard,
                        ...(isSelected ? styles.fiscalModeCardSelected : {}),
                      }}
                      disabled={saving || !canSelectFiscalMode}
                    >
                      <span style={styles.fiscalModeCardHeader}>
                        <strong style={styles.fiscalModeCardTitle}>{option.label}</strong>
                        <span
                          style={{
                            ...styles.fiscalModeBadge,
                            ...(isSelected ? styles.fiscalModeBadgeSelected : {}),
                          }}
                        >
                          {option.badge}
                        </span>
                      </span>
                      <span style={styles.fiscalModeDescription}>{option.description}</span>
                    </button>
                  );
                })}
              </div>

              <div style={styles.fiscalModeHelper}>
                <strong>
                  {getCompanyFiscalModeOption(companyProfileForm.fiscalMode).label}
                </strong>
                <span>
                  {getCompanyFiscalModeOption(companyProfileForm.fiscalMode).helper}
                </span>
              </div>

              {companyProfileForm.fiscalMode ===
              COMPANY_FISCAL_MODES.ARCA_INTEGRATED ? (
                <ArcaFiscalConnectionPanel
                  companyId={currentCompany?.id || ""}
                  companyProfile={companyProfileForm}
                  branches={availableBranches}
                  canManage={canManageFiscalConnection}
                  currentAccessRoleLabel={currentAccessRoleLabel}
                  previewMode={isCreatorMode}
                />
              ) : null}
            </fieldset>

            <input
              type="text"
              value={companyProfileForm.companyName}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  companyName: event.target.value,
                }))
              }
              placeholder="Nombre comercial de la empresa"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.legalName}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  legalName: event.target.value,
                }))
              }
              placeholder="Razon social"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.companyPhone}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  companyPhone: event.target.value,
                }))
              }
              placeholder="Telefono administrativo"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="email"
              value={companyProfileForm.billingEmail}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  billingEmail: event.target.value,
                }))
              }
              placeholder="Email de facturacion"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.supportPhone}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  supportPhone: event.target.value,
                }))
              }
              placeholder="Telefono de soporte"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.taxId}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  taxId: event.target.value,
                }))
              }
              placeholder="CUIT / identificacion fiscal"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.ownerFullName}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  ownerFullName: event.target.value,
                }))
              }
              placeholder="Nombre y apellido del titular"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.ownerDocumentId}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  ownerDocumentId: event.target.value,
                }))
              }
              placeholder="Documento del titular"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.ownerPhone}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  ownerPhone: event.target.value,
                }))
              }
              placeholder="Telefono del titular"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="email"
              value={companyProfileForm.ownerEmail}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  ownerEmail: event.target.value,
                }))
              }
              placeholder="Email del titular"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.bankAccountHolder}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  bankAccountHolder: event.target.value,
                }))
              }
              placeholder="Titular de la cuenta"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.bankName}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  bankName: event.target.value,
                }))
              }
              placeholder="Banco"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <select
              value={companyProfileForm.bankAccountType}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  bankAccountType: event.target.value,
                }))
              }
              style={styles.input}
              disabled={configurationSettingsDisabled}
            >
              <option value="Cuenta corriente">Cuenta corriente</option>
              <option value="Caja de ahorro">Caja de ahorro</option>
              <option value="Cuenta sueldo">Cuenta sueldo</option>
              <option value="Billetera virtual">Billetera virtual</option>
            </select>
            <input
              type="text"
              value={companyProfileForm.bankAccountNumber}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  bankAccountNumber: event.target.value,
                }))
              }
              placeholder="Numero de cuenta"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.bankCbu}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  bankCbu: event.target.value,
                }))
              }
              placeholder="CBU"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.bankAlias}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  bankAlias: event.target.value,
                }))
              }
              placeholder="Alias"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={companyProfileForm.headquartersAddress}
              onChange={(event) =>
                setCompanyProfileForm((prev) => ({
                  ...prev,
                  headquartersAddress: event.target.value,
                }))
              }
              placeholder="Domicilio legal / administrativo"
              style={{ ...styles.input, gridColumn: "1 / -1" }}
              disabled={configurationSettingsDisabled}
            />

            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={configurationSettingsDisabled}
              >
                {saving ? "Guardando..." : "Guardar datos de empresa"}
              </button>
            </div>
          </form>

          <div style={{ display: "grid", gap: "12px" }}>
            <p style={styles.sectionDescription}>
              Los cobros digitales se configuran por sucursal. Aqui administras la
              cuenta conectada y los metodos visibles de la sucursal activa del panel:
              <strong>{` ${currentBranch?.name || availableBranches[0]?.name || "sin sucursal"}`}</strong>.
            </p>
            {!canManageConfigurationPayments ? (
              <div style={styles.banner}>
                {currentAccessRoleLabel} puede ver el estado de cobros, pero no conectar
                cuentas ni cambiar medios de pago desde esta sucursal.
              </div>
            ) : null}
            <BranchDigitalPaymentsPanel
              branch={currentBranch || availableBranches[0] || null}
              disabled={!canManageConfigurationPayments}
              currentAccessRoleLabel={currentAccessRoleLabel}
            />
          </div>
        </div>
      )}

      {activeSection === "sucursales" && (
        <div style={styles.sectionBand}>
          <div style={styles.sectionIntro}>
            <h2 style={styles.sectionTitle}>Sucursales</h2>
            <p style={styles.sectionDescription}>
              Crea nuevas sucursales y cambia rapidamente la sucursal activa del panel.
            </p>
            <p style={styles.sectionWarning}>
              Por ahora, eliminar una sucursal requiere confirmar escribiendo su nombre. En
              una etapa siguiente lo vamos a reemplazar por un codigo de seguridad enviado al
              correo.
            </p>
          </div>

          {!canManageConfigurationSettings ? (
            <div style={styles.banner}>
              {currentAccessRoleLabel} puede revisar sucursales y modos operativos, pero no
              modificar datos de sucursal, launchers, cocina ni acciones auxiliares.
            </div>
          ) : null}

          <div style={styles.listGrid}>
            {availableBranches.map((branch) => {
              const branchMenuSetup = branchMenuById[branch.id];
              const branchProfile = buildResolvedBranchOperationalProfile(branch);
              const branchServiceMode = branchProfile.serviceMode || "salon";
              const branchKitchenMode = normalizeKitchenModeForServiceMode(
                branchServiceMode,
                branchKitchenModeById[branch.id] ||
                  branchProfile.kitchenMode ||
                  "manual"
              );
              const branchKioskOption = resolveKioskOptionId(branchProfile);
              const branchKioskStationCount = normalizeKioskStationCount(
                branchKioskStationCountById[branch.id] ??
                  branchProfile.kioskStationCount
              );
              const availableKitchenModeOptions =
                getKitchenModeOptionsForServiceMode(branchServiceMode);
              const selectedKitchenMode =
                availableKitchenModeOptions.find((option) => option.id === branchKitchenMode) ||
                availableKitchenModeOptions[0];
              const selectedServiceMode =
                BRANCH_SERVICE_MODE_OPTIONS.find(
                  (option) => option.id === branchServiceMode
                ) || BRANCH_SERVICE_MODE_OPTIONS[0];
              const selectedKioskOption =
                KIOSK_EXPERIENCE_OPTIONS.find(
                  (option) => option.id === branchKioskOption
                ) || KIOSK_EXPERIENCE_OPTIONS[0];
              const branchDisplayPairingCode =
                branchScreenPairingCodeById[branch.id] || "";

              return (
              <div key={branch.id} style={styles.itemPanel}>
                <div style={styles.itemHeaderRow}>
                  <div>
                    <div style={styles.itemTitle}>{branch.name}</div>
                    <div style={styles.itemMeta}>
                      {branch.address || "Sin domicilio"} · {branch.phone || "Sin telefono"}
                    </div>
                  </div>
                  <div style={styles.branchActions}>
                    <button
                      type="button"
                      onClick={() => setActiveBranch(branch.id)}
                      style={{
                        ...styles.secondaryMiniButton,
                        ...(currentBranch?.id === branch.id
                          ? styles.secondaryMiniButtonActive
                          : {}),
                      }}
                    >
                      {currentBranch?.id === branch.id ? "Activa" : "Usar"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteBranch(branch)}
                      style={styles.dangerMiniButton}
                      disabled={
                        configurationSettingsDisabled || availableBranches.length <= 1
                      }
                      title={
                        availableBranches.length <= 1
                          ? "Necesitas al menos una sucursal activa"
                          : "Eliminar sucursal"
                      }
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
                <div style={styles.itemBodyText}>
                  {branch.description || "Sucursal sin descripcion cargada."}
                </div>
                <BranchOperationalModulesPanel
                  branch={branch}
                  profile={branchProfile}
                  disabled={branchConfigurationDisabled}
                  heading={`Como opera ${branch.name}`}
                  onSaved={() => {
                    setErrorMessage("");
                    setSuccessMessage(
                      `Actualizamos los canales y herramientas de ${branch.name}.`
                    );
                  }}
                />
                <div style={styles.branchMenuPanel}>
                  <div style={styles.itemHeaderRow}>
                    <div>
                      <div style={styles.branchMenuTitle}>Menu de la sucursal</div>
                      <div style={styles.branchMenuMeta}>
                        {branchMenuSetup?.currentMenuName || "Cargando menu..."}
                      </div>
                    </div>
                    {branchMenuSetup ? (
                      <span
                        style={{
                          ...styles.branchMenuBadge,
                          ...(branchMenuSetup.isUsingSharedMenu
                            ? styles.branchMenuBadgeShared
                            : styles.branchMenuBadgeCustom),
                        }}
                      >
                        {branchMenuSetup.isUsingSharedMenu ? "Compartido" : "Propio"}
                      </span>
                    ) : (
                      <span style={{ ...styles.branchMenuBadge, ...styles.branchMenuBadgeLoading }}>
                        Cargando
                      </span>
                    )}
                  </div>
                  <div style={styles.branchMenuHint}>
                    {!branchMenuSetup
                      ? "Estamos consultando que carta usa esta sucursal."
                      : branchMenuSetup.isUsingSharedMenu
                      ? "Esta sucursal comparte la carta de la empresa. Si alguien toca este menu, impacta en las otras sucursales compartidas."
                      : "Esta sucursal tiene una carta propia y puede personalizarla sin afectar al resto."}
                  </div>
                  <div style={styles.branchMenuActions}>
                    {!branchMenuSetup ? null : branchMenuSetup.isUsingSharedMenu ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleCreateCustomMenu(branch, false)}
                          style={styles.secondaryMiniButton}
                          disabled={branchConfigurationDisabled}
                        >
                          Copiar y personalizar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCreateCustomMenu(branch, true)}
                          style={styles.secondaryMiniButton}
                          disabled={branchConfigurationDisabled}
                        >
                          Crear menu nuevo
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAssignSharedMenu(branch)}
                        style={styles.secondaryMiniButton}
                        disabled={branchConfigurationDisabled}
                      >
                        Volver a compartido
                      </button>
                    )}
                  </div>
                </div>

                <div style={styles.branchKitchenPanel}>
                  <div style={styles.itemHeaderRow}>
                    <div>
                      <div style={styles.branchMenuTitle}>Modalidad de la sucursal</div>
                      <div style={styles.branchKitchenMeta}>
                        {selectedServiceMode.badge}
                      </div>
                    </div>
                    <span style={styles.branchKitchenBadge}>
                      {selectedServiceMode.label}
                    </span>
                  </div>
                  <div style={styles.branchKitchenHint}>
                    {selectedServiceMode.description}
                  </div>
                  <div style={styles.branchMenuHint}>
                    Esta modalidad se define al crear la sucursal y ya no se puede modificar.
                    Si mas adelante quieres cambiar entre restaurante y kiosk, crea otra
                    sucursal.
                  </div>
                  {selectedKioskOption.id !== "disabled" && (
                    <>
                      <div style={styles.branchKioskStationPanel}>
                        <div>
                          <div style={styles.branchKitchenTitle}>Cantidad de totems</div>
                          <div style={styles.branchKitchenHint}>
                            Define cuantos puestos de autoservicio puede abrir esta sucursal.
                          </div>
                        </div>
                        <div style={styles.branchKioskStationStepper}>
                          <button
                            type="button"
                            onClick={() =>
                              handleBranchKioskStationCountChange(
                                branch,
                                branchKioskStationCount - 1
                              )
                            }
                            style={styles.branchKioskStationButton}
                            disabled={
                              branchConfigurationDisabled ||
                              branchKioskStationCount <= 1
                            }
                          >
                            -
                          </button>
                          <div style={styles.branchKioskStationValue}>
                            {branchKioskStationCount}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handleBranchKioskStationCountChange(
                                branch,
                                branchKioskStationCount + 1
                              )
                            }
                            style={styles.branchKioskStationButton}
                            disabled={
                              branchConfigurationDisabled ||
                              branchKioskStationCount >= 12
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div style={styles.branchKioskAccessPanel}>
                        <div style={styles.branchKitchenTitle}>Abrir totems</div>
                        <div style={styles.branchKitchenHint}>
                          Cada boton abre una pantalla de venta dedicada para ese puesto.
                        </div>
                        <div style={styles.branchKitchenActions}>
                          <button
                            type="button"
                              onClick={() => {
                              setActiveBranch(branch.id);
                              navigate(`/totem/${branch.id}?setup=1`);
                            }}
                            style={styles.secondaryMiniButton}
                            disabled={!canManageConfigurationSettings}
                          >
                            Pantalla principal
                          </button>
                          {Array.from({ length: branchKioskStationCount }, (_, index) => {
                            const stationNumber = index + 1;

                            return (
                              <button
                                key={`${branch.id}-station-${stationNumber}`}
                                type="button"
                                onClick={() => handleOpenKioskStation(branch, stationNumber)}
                                style={styles.secondaryMiniButton}
                                disabled={!canManageConfigurationSettings}
                              >
                                Totem {stationNumber}
                              </button>
                            );
                          })}
                        </div>
                        <div style={styles.branchKitchenHint}>
                          El launcher Windows usa WebView2, pantalla completa y configuracion
                          local de impresora para tickets de 58/80 mm. El primer arranque prepara
                          los componentes locales si todavia no estan instalados. En Android usamos
                          un QR y un acceso directo para dejar la tablet o celular apuntando a este kiosk.
                        </div>
                        <div style={styles.branchKitchenActions}>
                          <button
                            type="button"
                            onClick={() => handleDownloadKioskWindowsLauncher(branch)}
                            style={styles.secondaryMiniButton}
                            disabled={!canManageConfigurationSettings}
                          >
                            Descargar acceso Windows
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenKioskAndroidLauncher(branch)}
                            style={styles.secondaryMiniButton}
                            disabled={!canManageConfigurationSettings}
                          >
                            Launcher Android
                          </button>
                        </div>
                        <div style={styles.branchDisplayPanel}>
                          <div style={styles.branchKitchenTitle}>
                            Pantalla de preparacion y retiro
                          </div>
                          <div style={styles.branchKitchenHint}>
                            En el otro dispositivo entra a {" "}
                            <a
                              href="https://mozzoapp.com/display"
                              target="_blank"
                              rel="noreferrer"
                              style={styles.branchPublicAccessLink}
                            >
                              mozzoapp.com/display
                            </a>{" "}
                            y escribe este codigo para vincular la pantalla con esta sucursal.
                          </div>
                          <div style={styles.branchDisplayCode}>
                            {branchDisplayPairingCode || "Generando codigo seguro..."}
                          </div>
                          <div style={styles.branchKitchenActions}>
                            <button
                              type="button"
                              onClick={() => handleOpenPublicDisplay(branch)}
                              style={styles.secondaryMiniButton}
                              disabled={
                                !canManageConfigurationSettings || !branchDisplayPairingCode
                              }
                            >
                              Abrir display
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopyPublicDisplayCode(branch)}
                              style={styles.secondaryMiniButton}
                              disabled={
                                !canManageConfigurationSettings || !branchDisplayPairingCode
                              }
                            >
                              Copiar codigo
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopyPublicDisplayLink(branch)}
                              style={styles.secondaryMiniButton}
                              disabled={
                                !canManageConfigurationSettings || !branchDisplayPairingCode
                              }
                            >
                              Copiar link publico
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRotatePublicScreenCode(branch)}
                              style={styles.secondaryMiniButton}
                              disabled={!canManageConfigurationSettings || saving}
                            >
                              Regenerar codigo
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div style={styles.branchKitchenPanel}>
                  <div style={styles.itemHeaderRow}>
                    <div>
                      <div style={styles.branchKitchenTitle}>Operacion de cocina</div>
                      <div style={styles.branchKitchenMeta}>
                        {selectedKitchenMode.badge}
                      </div>
                    </div>
                    <span style={styles.branchKitchenBadge}>
                      {selectedKitchenMode.badge}
                    </span>
                  </div>
                  <div style={styles.branchKitchenHint}>
                    {selectedKitchenMode.description}
                  </div>
                  {branchServiceMode === "kiosk" ? (
                    <div style={styles.branchMenuHint}>
                      En kiosk, si eliges impresora, abre tambien el display de preparacion y retiro para que los clientes sigan su numero.
                    </div>
                  ) : null}
                  <div style={styles.branchKitchenActions}>
                    {availableKitchenModeOptions.map((option) => (
                      <button
                        key={`${branch.id}-${option.id}`}
                        type="button"
                        onClick={() => handleBranchKitchenModeChange(branch, option.id)}
                        style={{
                          ...styles.secondaryMiniButton,
                          ...(branchKitchenMode === option.id
                            ? styles.secondaryMiniButtonActive
                            : {}),
                        }}
                        disabled={!canManageConfigurationSettings}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {branchKitchenMode === "screen" ? (
                    <div style={styles.branchDisplayPanel}>
                      <div style={styles.branchKitchenTitle}>Cocina por codigo</div>
                      <div style={styles.branchKitchenHint}>
                        En el dispositivo de cocina entra a {" "}
                        <a
                          href="https://mozzoapp.com/cocina"
                          target="_blank"
                          rel="noreferrer"
                          style={styles.branchPublicAccessLink}
                        >
                          mozzoapp.com/cocina
                        </a>{" "}
                        y escribe este codigo para vincularlo con esta sucursal.
                      </div>
                      <div style={styles.branchDisplayCode}>
                        {branchDisplayPairingCode || "Generando codigo seguro..."}
                      </div>
                      <div style={styles.branchKitchenActions}>
                        <button
                          type="button"
                          onClick={() => handleOpenPublicKitchen(branch)}
                          style={styles.secondaryMiniButton}
                          disabled={
                            !canManageConfigurationSettings || !branchDisplayPairingCode
                          }
                        >
                          Abrir cocina
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyPublicKitchenCode(branch)}
                          style={styles.secondaryMiniButton}
                          disabled={
                            !canManageConfigurationSettings || !branchDisplayPairingCode
                          }
                        >
                          Copiar codigo
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyPublicKitchenLink(branch)}
                          style={styles.secondaryMiniButton}
                          disabled={
                            !canManageConfigurationSettings || !branchDisplayPairingCode
                          }
                        >
                          Copiar link publico
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRotatePublicScreenCode(branch)}
                          style={styles.secondaryMiniButton}
                          disabled={!canManageConfigurationSettings || saving}
                        >
                          Regenerar codigo
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )})}
          </div>

          <form onSubmit={handleCreateBranch} style={styles.formGrid}>
            <input
              type="text"
              value={branchForm.name}
              onChange={(event) =>
                setBranchForm((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Nombre de la sucursal"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={branchForm.phone}
              onChange={(event) =>
                setBranchForm((prev) => ({ ...prev, phone: event.target.value }))
              }
              placeholder="Telefono"
              style={styles.input}
              disabled={configurationSettingsDisabled}
            />
            <input
              type="text"
              value={branchForm.address}
              onChange={(event) =>
                setBranchForm((prev) => ({ ...prev, address: event.target.value }))
              }
              placeholder="Domicilio"
              style={{ ...styles.input, gridColumn: "1 / -1" }}
              disabled={configurationSettingsDisabled}
            />
            <textarea
              value={branchForm.description}
              onChange={(event) =>
                setBranchForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              placeholder="Descripcion breve de la sucursal"
              style={{ ...styles.input, ...styles.textarea, gridColumn: "1 / -1" }}
              disabled={configurationSettingsDisabled}
            />
            <div style={{ ...styles.branchCreationModePanel, gridColumn: "1 / -1" }}>
              <div style={styles.branchCreationModeHeader}>
                <div style={styles.branchMenuTitle}>Modalidad operativa</div>
                <div style={styles.branchMenuHint}>
                  Esta decision queda fija para la sucursal. Si luego quieres otra
                  modalidad, crea una sucursal nueva.
                </div>
              </div>
              <div style={styles.branchKitchenActions}>
                {BRANCH_SERVICE_MODE_OPTIONS.map((option) => (
                  <button
                    key={`create-branch-mode-${option.id}`}
                    type="button"
                    onClick={() =>
                      setBranchForm((prev) => {
                        const isKiosk = option.id === "kiosk";
                        return {
                          ...prev,
                          serviceMode: option.id,
                          salonEnabled: isKiosk ? false : true,
                          waiterEnabled: isKiosk ? false : prev.waiterEnabled,
                        };
                      })
                    }
                    style={{
                      ...styles.branchCreationModeButton,
                      ...(branchForm.serviceMode === option.id
                        ? styles.branchCreationModeButtonActive
                        : {}),
                    }}
                    disabled={configurationSettingsDisabled}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ ...styles.branchCreationModePanel, gridColumn: "1 / -1" }}>
              <div style={styles.branchCreationModeHeader}>
                <div style={styles.branchMenuTitle}>Integraciones y modulos iniciales</div>
                <div style={styles.branchMenuHint}>
                  Puedes cambiarlos despues por sucursal. We! pedidos requiere Delivery y
                  los mozos requieren Salon.
                </div>
              </div>
              <div className="branch-modules__grid">
                {BRANCH_MODULE_FORM_OPTIONS.map((option) => {
                  const moduleDisabled =
                    configurationSettingsDisabled ||
                    (option.key === "salonEnabled" && branchForm.serviceMode === "kiosk") ||
                    (option.key === "waiterEnabled" && !branchForm.salonEnabled) ||
                    (option.key === "wePedidosEnabled" && !branchForm.deliveryEnabled);
                  const active = branchForm[option.key] === true;

                  return (
                    <label
                      key={`create-branch-module-${option.key}`}
                      className={`branch-module-card${
                        moduleDisabled ? " branch-module-card--disabled" : ""
                      }`}
                    >
                      <span className="branch-module-card__copy">
                        <strong>{option.title}</strong>
                        <span>{option.description}</span>
                      </span>
                      <span className="branch-module-toggle">
                        <input
                          type="checkbox"
                          checked={active}
                          disabled={moduleDisabled}
                          onChange={(event) =>
                            setBranchForm((prev) =>
                              applyBranchOperationalModuleToggle(
                                prev,
                                option.key,
                                event.target.checked
                              )
                            )
                          }
                        />
                        <span className="branch-module-toggle__track" aria-hidden="true">
                          <span className="branch-module-toggle__thumb" />
                        </span>
                        <span className="branch-module-toggle__label">
                          {active ? "Activo" : "Pausado"}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            {branchForm.serviceMode === "kiosk" ? (
              <div style={{ ...styles.branchCreationModePanel, gridColumn: "1 / -1" }}>
                <div style={styles.branchCreationModeHeader}>
                  <div style={styles.branchMenuTitle}>Carta inicial del kiosk</div>
                  <div style={styles.branchMenuHint}>
                    Elige si la sucursal kiosk arranca con la carta actual compartida o con
                    una carta propia nueva.
                  </div>
                </div>
                <div style={styles.branchKitchenActions}>
                  {KIOSK_EXPERIENCE_OPTIONS.filter((option) => option.id !== "disabled").map(
                    (option) => (
                      <button
                        key={`create-branch-kiosk-menu-${option.id}`}
                        type="button"
                        onClick={() =>
                          setBranchForm((prev) => ({
                            ...prev,
                            kioskMenuMode: option.kioskMenuMode,
                          }))
                        }
                        style={{
                          ...styles.branchCreationModeButton,
                          ...(branchForm.kioskMenuMode === option.kioskMenuMode
                            ? styles.branchCreationModeButtonActive
                            : {}),
                        }}
                        disabled={configurationSettingsDisabled}
                      >
                        <strong>{option.label}</strong>
                        <span>{option.description}</span>
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : null}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={configurationSettingsDisabled}
              >
                {saving ? "Guardando..." : "Agregar sucursal"}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeSection === "admins" && (
        <div style={styles.sectionBand}>
          <div style={styles.sectionIntro}>
            <h2 style={styles.sectionTitle}>Usuarios y accesos</h2>
            <p style={styles.sectionDescription}>
              Crea usuarios internos, asignales rol operativo, sucursal y estado activo
              usando la misma base central de permisos.
            </p>
          </div>

          {!canManageConfigurationUsers ? (
            <div style={styles.banner}>
              {currentAccessRoleLabel} puede revisar usuarios y permisos, pero no crear,
              editar ni desactivar accesos internos en esta empresa.
            </div>
          ) : null}

          <form onSubmit={handleCreateInvitation} style={styles.inviteForm}>
            <div style={styles.permissionsPanel}>
              <div style={styles.permissionsPanelHeader}>
                <div style={styles.permissionsEyebrow}>{editingModeLabel}</div>
                <div style={styles.permissionsLead}>
                  {selectedInviteRole?.description ||
                    "Define el rol operativo y deja preparada la sucursal del usuario."}
                </div>
              </div>

              {isEditingInternalUser ? (
                <div style={styles.infoInlineBand}>
                  Estas editando un acceso existente. Puedes cambiar rol, sucursal y estado
                  activo sin duplicar reglas en la UI.
                </div>
              ) : null}
            </div>

            <select
              value={inviteForm.roleKey}
              onChange={(event) => applyInternalUserRoleSelection(event.target.value)}
              style={styles.input}
              disabled={configurationUsersDisabled}
            >
              {internalUserRoleOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>

            <input
              type="email"
              value={inviteForm.email}
              onChange={(event) =>
                setInviteForm((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder={
                editingUserId
                  ? "Correo ya vinculado al usuario"
                  : "Correo del usuario interno"
              }
              style={styles.input}
              disabled={configurationUsersDisabled || Boolean(editingUserId)}
            />

            {shouldShowFullNameField(inviteForm.roleKey) ? (
              <input
                type="text"
                value={inviteForm.fullName}
                onChange={(event) =>
                  setInviteForm((prev) => ({ ...prev, fullName: event.target.value }))
                }
                placeholder="Nombre visible del usuario"
                style={styles.input}
                disabled={configurationUsersDisabled}
              />
            ) : null}

            {selectedInviteRole?.scope === "branch" ? (
              <select
                value={inviteForm.branchId}
                onChange={(event) =>
                  setInviteForm((prev) => ({ ...prev, branchId: event.target.value }))
                }
                style={styles.input}
                disabled={configurationUsersDisabled}
              >
                <option value="">Selecciona sucursal</option>
                {availableBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            ) : (
              <div style={styles.readonlyField}>Acceso empresa completa</div>
            )}

            {editingUserId ? (
              <label style={styles.checkboxInline}>
                <input
                  type="checkbox"
                  checked={inviteForm.active !== false}
                  onChange={(event) =>
                    setInviteForm((prev) => ({ ...prev, active: event.target.checked }))
                  }
                  disabled={configurationUsersDisabled}
                />
                Usuario activo
              </label>
            ) : (
              <div style={styles.readonlyField}>Estado inicial: activo</div>
            )}

            {isConfigurableBranchRole(inviteForm.roleKey) ? (
              <div style={styles.permissionsPanel}>
                <div style={styles.permissionsPanelHeader}>
                  <div style={styles.permissionsEyebrow}>Permisos configurables</div>
                  <div style={styles.permissionsLead}>
                    Este rol trabaja por sucursal. Gestionar siempre incluye lectura.
                  </div>
                </div>

                <div style={styles.permissionsGrid}>
                  {MOZZO_BRANCH_PERMISSION_GROUPS.map((group) => {
                    const viewEnabled = group.viewKey
                      ? inviteForm[group.viewKey] || inviteForm[group.manageKey]
                      : inviteForm[group.manageKey];
                    const manageEnabled = inviteForm[group.manageKey];
                    const statusLabel = group.viewKey
                      ? manageEnabled
                        ? "Gestion total"
                        : viewEnabled
                          ? "Solo lectura"
                          : "Sin acceso"
                      : manageEnabled
                        ? "Habilitado"
                        : "Sin acceso";

                    return (
                      <div key={group.id} style={styles.permissionCard}>
                        <div style={styles.permissionHeader}>
                          <div style={styles.permissionHeaderCopy}>
                            <strong style={styles.permissionTitle}>{group.label}</strong>
                            <span style={styles.permissionDescription}>{group.description}</span>
                          </div>
                          <span
                            style={{
                              ...styles.permissionState,
                              ...(manageEnabled
                                ? styles.permissionStateManage
                                : viewEnabled
                                  ? styles.permissionStateView
                                  : styles.permissionStateOff),
                            }}
                          >
                            {statusLabel}
                          </span>
                        </div>

                        {group.viewKey ? (
                          <div style={styles.permissionToggleRow}>
                            <button
                              type="button"
                              onClick={() =>
                                updateInvitePermission(group, "viewKey", !viewEnabled)
                              }
                              disabled={configurationUsersDisabled}
                              style={{
                                ...styles.permissionToggleButton,
                                ...(viewEnabled
                                  ? styles.permissionToggleButtonActive
                                  : {}),
                              }}
                            >
                              Ver
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                updateInvitePermission(group, "manageKey", !manageEnabled)
                              }
                              disabled={configurationUsersDisabled}
                              style={{
                                ...styles.permissionToggleButton,
                                ...(manageEnabled
                                  ? styles.permissionToggleButtonManage
                                  : {}),
                              }}
                            >
                              Gestionar
                            </button>
                          </div>
                        ) : (
                          <div style={styles.permissionToggleRow}>
                            <button
                              type="button"
                              onClick={() =>
                                setInviteForm((prev) => ({
                                  ...prev,
                                  [group.manageKey]: !prev[group.manageKey],
                                }))
                              }
                              disabled={configurationUsersDisabled}
                              style={{
                                ...styles.permissionToggleButton,
                                ...(manageEnabled
                                  ? styles.permissionToggleButtonManage
                                  : {}),
                              }}
                            >
                              Habilitar acceso
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={styles.permissionsPanel}>
                <div style={styles.permissionsPanelHeader}>
                  <div style={styles.permissionsEyebrow}>Resumen del rol</div>
                  <div style={styles.permissionsLead}>
                    Este rol usa una plantilla fija desde accessControl.js. En esta etapa no
                    necesita editor visual avanzado.
                  </div>
                </div>

                <div style={styles.permissionsWrap}>
                  {selectedInviteRoleModules.map((moduleLabel) => (
                    <span key={moduleLabel} style={styles.tag}>
                      {moduleLabel}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.formActionRow}>
              {isEditingInternalUser ? (
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => resetInternalUserForm(inviteForm.branchId)}
                  disabled={configurationUsersDisabled}
                >
                  Cancelar edicion
                </button>
              ) : null}
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={configurationUsersDisabled}
              >
                {saving ? "Guardando..." : isEditingInternalUser ? "Guardar cambios" : "Crear usuario"}
              </button>
            </div>
          </form>

          <div style={styles.listGrid}>
            {invitations.length === 0 ? (
              <div style={styles.emptyState}>Todavia no hay invitaciones cargadas.</div>
            ) : (
              invitations.map((invitation) => (
                <div key={invitation.id} style={styles.itemPanel}>
                  <div style={styles.itemHeaderRow}>
                    <div>
                      <div style={styles.itemTitle}>
                        {invitation.display_name || invitation.email}
                      </div>
                      <div style={styles.itemMeta}>
                        {getInvitationBranchName(availableBranches, invitation.branch_id)} -{" "}
                        {formatInvitationStatus(invitation.status)}
                      </div>
                    </div>
                    {isProtectedOwnerInvitation(invitation) ? (
                      <span style={styles.ownerLockTag}>Titular vinculado</span>
                    ) : invitation.status === "pending" ? (
                      <div style={styles.cardActionGroup}>
                        <button
                          type="button"
                          onClick={() => handleEditInvitation(invitation)}
                          style={styles.secondaryMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelInvitation(invitation.id)}
                          style={styles.secondaryMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyInvitationLink(invitation)}
                          style={styles.secondaryMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Copiar link
                        </button>
                      </div>
                    ) : (
                      <div style={styles.cardActionGroup}>
                        <button
                          type="button"
                          onClick={() => handleEditInvitation(invitation)}
                          style={styles.secondaryMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteInvitation(invitation.id)}
                          style={styles.dangerMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Eliminar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyInvitationLink(invitation)}
                          style={styles.secondaryMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Copiar link
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={styles.permissionsWrap}>
                    <span style={styles.tag}>
                      {getWorkspaceRoleLabel(invitation.workspace_role, invitation.role)}
                    </span>
                    {buildRoleSummaryTags(
                      resolveMozzoInternalUserRoleKey({
                        role: invitation.role,
                        workspaceRole: invitation.workspace_role,
                      }),
                      buildInternalUserFormFromInvitation(invitation),
                      availableBranches.find((branch) => branch.id === invitation.branch_id)
                    ).map((tag) => (
                      <span key={tag} style={styles.tag}>
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div style={styles.itemFooter}>
                    Creada: {formatDate(invitation.created_at)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.sectionIntro}>
            <h3 style={styles.sectionSubtitle}>Usuarios de la empresa</h3>
            <p style={styles.sectionDescription}>
              Desde aca puedes editar, desactivar o reactivar usuarios ya aceptados sin
              duplicar logica de permisos.
            </p>
          </div>

          <div style={styles.listGrid}>
            {manageableTeamUsers.length === 0 ? (
              <div style={styles.emptyState}>
                Todavia no hay usuarios internos cargados para esta empresa.
              </div>
            ) : (
              manageableTeamUsers.map((companyUser) => {
                const workspaceRole = companyUser.workspace_role || companyUser.role;
                const roleKey = resolveMozzoInternalUserRoleKey({
                  role: companyUser.role,
                  workspaceRole,
                });
                const summaryTags = buildRoleSummaryTags(
                  roleKey,
                  companyUser.branchAccesses?.find((branchAccess) => branchAccess.active)
                    ?.permissions ||
                    {},
                  availableBranches.find((branch) => branch.id === companyUser.primaryBranchId)
                );

                return (
                  <div key={companyUser.user_id} style={styles.itemPanel}>
                    <div style={styles.itemHeaderRow}>
                      <div>
                        <div style={styles.itemTitle}>
                          {formatCompanyUserLabel(companyUser, invitationEmailByUser)}
                        </div>
                        <div style={styles.itemMeta}>
                          {getWorkspaceRoleLabel(
                            workspaceRole,
                            companyUser.role,
                            isProtectedOwnerUser(companyUser)
                          )}{" "}
                          - {companyUser.active === false ? "inactivo" : "activo"} -{" "}
                          {companyUser.primaryBranchName || "Empresa completa"}
                        </div>
                      </div>
                      <div style={styles.cardActionGroup}>
                        <button
                          type="button"
                          onClick={() => handleEditCompanyUser(companyUser)}
                          style={styles.secondaryMiniButton}
                          disabled={configurationUsersDisabled}
                        >
                          Editar
                        </button>
                        {companyUser.active === false ? (
                          <button
                            type="button"
                            onClick={() => handleRestoreCompanyUser(companyUser)}
                            style={styles.secondaryMiniButton}
                            disabled={configurationUsersDisabled}
                          >
                            Reactivar
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemoveCompanyUser(companyUser)}
                            style={styles.dangerMiniButton}
                            disabled={configurationUsersDisabled}
                          >
                            Desactivar
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={styles.permissionsWrap}>
                      <span style={styles.tag}>
                        {getWorkspaceRoleLabel(
                          workspaceRole,
                          companyUser.role,
                          isProtectedOwnerUser(companyUser)
                        )}
                      </span>
                      {companyUser.profilePhone ? (
                        <span style={styles.tag}>{companyUser.profilePhone}</span>
                      ) : null}
                      {summaryTags.map((tag) => (
                        <span key={tag} style={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div style={styles.itemFooter}>
                      Alta: {formatDate(companyUser.created_at)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeSection === "turnos" && (
        <div style={styles.sectionBand}>
          <div style={styles.sectionIntro}>
            <h2 style={styles.sectionTitle}>Turnos</h2>
            <p style={styles.sectionDescription}>
              Registra los turnos de los admins limitados ya incorporados a la empresa.
            </p>
          </div>

          {!canManageConfigurationUsers ? (
            <div style={styles.banner}>
              {currentAccessRoleLabel} puede revisar los turnos, pero no crear ni eliminar
              turnos administrativos desde esta empresa.
            </div>
          ) : null}

          <form onSubmit={handleCreateShift} style={styles.formGrid}>
            <select
              value={shiftForm.userId}
              onChange={(event) =>
                setShiftForm((prev) => ({ ...prev, userId: event.target.value }))
              }
              style={styles.input}
              disabled={configurationUsersDisabled}
            >
              <option value="">Selecciona admin</option>
              {limitedAdmins.map((companyUser) => (
                <option key={companyUser.user_id} value={companyUser.user_id}>
                  {formatCompanyUserLabel(companyUser, invitationEmailByUser)}
                </option>
              ))}
            </select>

            <select
              value={shiftForm.branchId}
              onChange={(event) =>
                setShiftForm((prev) => ({ ...prev, branchId: event.target.value }))
              }
              style={styles.input}
              disabled={configurationUsersDisabled}
            >
              <option value="">Selecciona sucursal</option>
              {availableBranches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>

            <select
              value={shiftForm.dayOfWeek}
              onChange={(event) =>
                setShiftForm((prev) => ({ ...prev, dayOfWeek: event.target.value }))
              }
              style={styles.input}
              disabled={configurationUsersDisabled}
            >
              {dayOptions.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>

            <input
              type="time"
              value={shiftForm.startTime}
              onChange={(event) =>
                setShiftForm((prev) => ({ ...prev, startTime: event.target.value }))
              }
              style={styles.input}
              disabled={configurationUsersDisabled}
            />

            <input
              type="time"
              value={shiftForm.endTime}
              onChange={(event) =>
                setShiftForm((prev) => ({ ...prev, endTime: event.target.value }))
              }
              style={styles.input}
              disabled={configurationUsersDisabled}
            />

            <input
              type="text"
              value={shiftForm.notes}
              onChange={(event) =>
                setShiftForm((prev) => ({ ...prev, notes: event.target.value }))
              }
              placeholder="Notas del turno"
              style={{ ...styles.input, gridColumn: "1 / -1" }}
              disabled={configurationUsersDisabled}
            />

            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={configurationUsersDisabled}
              >
                {saving ? "Guardando..." : "Agregar turno"}
              </button>
            </div>
          </form>

          <div style={styles.listGrid}>
            {shifts.length === 0 ? (
              <div style={styles.emptyState}>
                Todavia no hay turnos cargados. Los turnos se pueden asignar cuando el admin
                limitado ya forma parte de la empresa.
              </div>
            ) : (
              shifts.map((shift) => (
                <div key={shift.id} style={styles.itemPanel}>
                  <div style={styles.itemHeaderRow}>
                    <div>
                      <div style={styles.itemTitle}>
                        {formatCompanyUserLabel(
                          limitedAdmins.find((companyUser) => companyUser.user_id === shift.user_id),
                          invitationEmailByUser
                        )}
                      </div>
                      <div style={styles.itemMeta}>
                        {shift.branch?.name || "Sucursal"} · {formatDay(shift.day_of_week)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteShift(shift.id)}
                      style={styles.secondaryMiniButton}
                      disabled={configurationUsersDisabled}
                    >
                      Eliminar
                    </button>
                  </div>
                  <div style={styles.itemBodyText}>
                    {shift.start_time} a {shift.end_time}
                  </div>
                  {shift.notes && <div style={styles.itemFooter}>{shift.notes}</div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {androidLauncherBranch ? (
        <div
          style={styles.kioskLauncherOverlay}
          onClick={handleCloseKioskAndroidLauncher}
        >
          <div
            style={styles.kioskLauncherDialog}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.kioskLauncherHeader}>
              <div>
                <div style={styles.kioskLauncherEyebrow}>MozzoApp Android</div>
                <h3 style={styles.kioskLauncherTitle}>
                  Launcher Android para {androidLauncherBranch.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseKioskAndroidLauncher}
                style={styles.dangerMiniButton}
              >
                Cerrar
              </button>
            </div>

            <div style={styles.kioskLauncherGrid}>
              <div style={styles.kioskLauncherQrPanel}>
                <div style={styles.branchKitchenTitle}>Paso 1: descargar APK</div>
                <div style={styles.branchKitchenHint}>
                  Escanealo desde la tablet Android para bajar e instalar Mozzo Kiosk.
                </div>
                <div style={styles.kioskLauncherQrFrame}>
                  {androidLauncherApkUrl ? (
                    <QRCode
                      value={androidLauncherApkUrl}
                      size={180}
                      bgColor="#ffffff"
                      fgColor="#0f172a"
                    />
                  ) : (
                    <div style={styles.emptyState}>Todavia no encontramos la APK compilada.</div>
                  )}
                </div>
                <div style={styles.branchKitchenActions}>
                  <button
                    type="button"
                    onClick={handleDownloadAndroidKioskApk}
                    style={styles.secondaryMiniButton}
                    disabled={!canManageConfigurationSettings}
                  >
                    Descargar APK
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyAndroidApkLink}
                    style={styles.secondaryMiniButton}
                    disabled={!canManageConfigurationSettings}
                  >
                    Copiar link APK
                  </button>
                </div>
              </div>

              <div style={styles.kioskLauncherInstructions}>
                <div style={styles.branchKitchenTitle}>Instalacion en tablet</div>
                <ol style={styles.kioskLauncherSteps}>
                  <li>Escanea el QR de descarga desde la tablet o abre el boton "Descargar APK".</li>
                  <li>Instala Mozzo Kiosk y, si Android lo pide, habilita instalar APK desde Chrome.</li>
                  <li>Cuando termine, vuelve a esta misma ventana para vincular el totem.</li>
                  <li>Despues del primer enlace, la app recordara ese puesto en la tablet.</li>
                </ol>
                <div style={styles.kioskLauncherCallout}>
                  No hace falta usuario ni contrasena propia en la APK. La vinculacion se hace
                  abriendo el launcher del totem correcto en el siguiente paso.
                </div>
              </div>
            </div>

            <div style={styles.kioskLauncherGrid}>
              <div style={styles.kioskLauncherQrPanel}>
                <div style={styles.branchKitchenTitle}>Paso 2: vincular launcher</div>
                <div style={styles.branchKitchenHint}>
                  Con la APK ya instalada, abre este QR o el link desde la misma tablet.
                </div>
                <div style={styles.kioskLauncherQrFrame}>
                  {androidLauncherMainUrl ? (
                    <QRCode
                      value={androidLauncherMainUrl}
                      size={180}
                      bgColor="#ffffff"
                      fgColor="#0f172a"
                    />
                  ) : (
                    <div style={styles.emptyState}>No pudimos generar el QR.</div>
                  )}
                </div>
                <div style={styles.branchKitchenActions}>
                  <button
                    type="button"
                    onClick={() => handleOpenAndroidLauncherLink(androidLauncherMainUrl)}
                    style={styles.secondaryMiniButton}
                    disabled={!canManageConfigurationSettings}
                  >
                    Abrir launcher
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleCopyAndroidLauncherLink(
                        androidLauncherMainUrl,
                        "principal del launcher Android"
                      )
                    }
                    style={styles.secondaryMiniButton}
                    disabled={!canManageConfigurationSettings}
                  >
                    Copiar link
                  </button>
                </div>
              </div>

              <div style={styles.kioskLauncherInstructions}>
                <div style={styles.branchKitchenTitle}>Como se vincula</div>
                <ol style={styles.kioskLauncherSteps}>
                  <li>Abre este launcher desde la misma tablet donde ya instalaste la APK.</li>
                  <li>Si entraste por la pantalla principal, toca el puesto correcto una sola vez.</li>
                  <li>Android te ofrecera abrir Mozzo Kiosk: aceptalo para dejarlo fijo ahi.</li>
                  <li>Desde ese momento, la APK abrira siempre ese totem en este equipo.</li>
                </ol>
                <div style={styles.kioskLauncherCallout}>
                  Si prefieres dejar cada equipo ya apuntando a un puesto puntual, tambien
                  puedes usar los links directos por totem de abajo.
                </div>
              </div>
            </div>

            <div style={styles.kioskLauncherStationSection}>
              <div style={styles.branchKitchenTitle}>Links directos por totem</div>
              <div style={styles.branchKitchenHint}>
                Cada tarjeta abre un puesto fijo. Sirve si quieres configurar una tablet por
                equipo sin pasar por la pantalla principal.
              </div>
              <div style={styles.kioskLauncherStationGrid}>
                {androidLauncherStationLinks.map((item) => (
                  <div
                    key={`${androidLauncherBranch.id}-android-launcher-${item.stationNumber}`}
                    style={styles.kioskLauncherStationCard}
                  >
                    <div style={styles.kioskLauncherStationTitle}>
                      Totem {item.stationNumber}
                    </div>
                    <div style={styles.kioskLauncherMiniQrFrame}>
                      <QRCode
                        value={item.url}
                        size={112}
                        bgColor="#ffffff"
                        fgColor="#0f172a"
                      />
                    </div>
                    <div style={styles.branchKitchenActions}>
                      <button
                        type="button"
                        onClick={() => handleOpenAndroidLauncherLink(item.url)}
                        style={styles.secondaryMiniButton}
                        disabled={!canManageConfigurationSettings}
                      >
                        Abrir
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopyAndroidLauncherLink(
                            item.url,
                            `del Totem ${item.stationNumber}`
                          )
                        }
                        style={styles.secondaryMiniButton}
                        disabled={!canManageConfigurationSettings}
                      >
                        Copiar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "24px",
  },
  headerBlock: {
    display: "grid",
    gap: "8px",
  },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#2563eb",
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    color: "#0f172a",
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.6,
    maxWidth: "860px",
  },
  sectionSwitch: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  sectionButton: {
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    borderRadius: "12px",
    padding: "12px 16px",
    cursor: "pointer",
    fontWeight: 600,
  },
  sectionButtonActive: {
    backgroundColor: "#0f172a",
    color: "#ffffff",
    borderColor: "#0f172a",
  },
  banner: {
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    padding: "14px 16px",
    color: "#475569",
  },
  bannerError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  bannerSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  sectionBand: {
    display: "grid",
    gap: "20px",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "22px",
    backgroundColor: "rgba(255,255,255,0.88)",
  },
  sectionIntro: {
    display: "grid",
    gap: "6px",
  },
  sectionTitle: {
    margin: 0,
    color: "#0f172a",
  },
  sectionSubtitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
  },
  sectionDescription: {
    margin: 0,
    color: "#64748b",
    lineHeight: 1.55,
  },
  sectionWarning: {
    margin: 0,
    color: "#9a3412",
    lineHeight: 1.55,
    fontSize: "13px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "12px",
  },
  fiscalModePanel: {
    gridColumn: "1 / -1",
    minWidth: 0,
    margin: 0,
    border: "1px solid #dbe6f3",
    borderRadius: "18px",
    padding: "18px",
    display: "grid",
    gap: "14px",
    background:
      "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(255,247,237,0.92) 100%)",
  },
  fiscalModeLegend: {
    padding: "0 8px",
    color: "#0f172a",
    fontWeight: 800,
    fontSize: "17px",
  },
  fiscalModeLead: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.55,
  },
  fiscalModeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "12px",
  },
  fiscalModeCard: {
    width: "100%",
    border: "1px solid #dbe2ea",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "16px",
    display: "grid",
    gap: "10px",
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
  },
  fiscalModeCardSelected: {
    borderColor: "#f97316",
    backgroundColor: "#fff7ed",
    boxShadow: "0 0 0 2px rgba(249,115,22,0.12)",
  },
  fiscalModeCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
  },
  fiscalModeCardTitle: {
    color: "#0f172a",
    fontSize: "15px",
    lineHeight: 1.35,
  },
  fiscalModeBadge: {
    flexShrink: 0,
    borderRadius: "999px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    padding: "5px 8px",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  },
  fiscalModeBadgeSelected: {
    borderColor: "#fdba74",
    backgroundColor: "#ffedd5",
    color: "#9a3412",
  },
  fiscalModeDescription: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  fiscalModeHelper: {
    borderRadius: "14px",
    border: "1px solid #fed7aa",
    backgroundColor: "rgba(255,255,255,0.82)",
    padding: "12px 14px",
    color: "#7c2d12",
    display: "grid",
    gap: "4px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  inviteForm: {
    display: "grid",
    gap: "14px",
  },
  formActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  readonlyField: {
    width: "100%",
    borderRadius: "12px",
    border: "1px dashed #cbd5e1",
    padding: "12px 14px",
    fontSize: "14px",
    boxSizing: "border-box",
    backgroundColor: "#f8fafc",
    color: "#475569",
  },
  checkboxInline: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "14px",
    color: "#334155",
  },
  textarea: {
    minHeight: "96px",
    resize: "vertical",
  },
  permissionsPanel: {
    display: "grid",
    gap: "16px",
    borderRadius: "18px",
    border: "1px solid #dbe6f3",
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)",
    padding: "18px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
  },
  permissionsPanelHeader: {
    display: "grid",
    gap: "6px",
  },
  permissionsEyebrow: {
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#2563eb",
  },
  permissionsLead: {
    color: "#475569",
    lineHeight: 1.6,
    fontSize: "14px",
    maxWidth: "760px",
  },
  permissionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "14px",
  },
  permissionCard: {
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    padding: "16px",
    display: "grid",
    gap: "14px",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
  },
  permissionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  permissionHeaderCopy: {
    display: "grid",
    gap: "6px",
  },
  permissionTitle: {
    color: "#0f172a",
    fontSize: "15px",
  },
  permissionDescription: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  permissionState: {
    flexShrink: 0,
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    border: "1px solid transparent",
  },
  permissionStateOff: {
    backgroundColor: "#f8fafc",
    color: "#64748b",
    borderColor: "#cbd5e1",
  },
  permissionStateView: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    borderColor: "#bfdbfe",
  },
  permissionStateManage: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
    borderColor: "#bbf7d0",
  },
  permissionToggleRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  permissionToggleButton: {
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#334155",
    borderRadius: "12px",
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  permissionToggleButtonActive: {
    backgroundColor: "#eff6ff",
    borderColor: "#93c5fd",
    color: "#1d4ed8",
    boxShadow: "0 0 0 1px rgba(59,130,246,0.08)",
  },
  permissionToggleButtonManage: {
    backgroundColor: "#0f172a",
    borderColor: "#0f172a",
    color: "#ffffff",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
  },
  permissionItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 600,
  },
  listGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "14px",
  },
  itemPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "16px",
    backgroundColor: "#ffffff",
    display: "grid",
    gap: "10px",
  },
  itemHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  branchActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  cardActionGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  branchMenuPanel: {
    marginTop: "4px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    display: "grid",
    gap: "10px",
  },
  branchKitchenPanel: {
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fff7ed",
    display: "grid",
    gap: "10px",
  },
  branchMenuTitle: {
    fontWeight: 700,
    color: "#0f172a",
  },
  branchKitchenTitle: {
    fontWeight: 700,
    color: "#0f172a",
  },
  branchMenuMeta: {
    marginTop: "4px",
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  branchKitchenMeta: {
    marginTop: "4px",
    color: "#9a3412",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  branchMenuHint: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  branchKitchenHint: {
    color: "#7c2d12",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  branchPublicAccessLink: {
    color: "#9a3412",
    fontWeight: 900,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  },
  branchMenuActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  branchKitchenActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  branchCreationModePanel: {
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #dbe3ee",
    backgroundColor: "#f8fafc",
    display: "grid",
    gap: "12px",
  },
  branchCreationModeHeader: {
    display: "grid",
    gap: "6px",
  },
  branchCreationModeButton: {
    flex: "1 1 240px",
    minHeight: "84px",
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "12px 14px",
    cursor: "pointer",
    display: "grid",
    gap: "6px",
    textAlign: "left",
  },
  branchCreationModeButtonActive: {
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    boxShadow: "0 0 0 1px rgba(147,197,253,0.4) inset",
  },
  branchKioskStationPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingTop: "8px",
    borderTop: "1px dashed rgba(154, 52, 18, 0.18)",
  },
  branchKioskStationStepper: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  branchKioskStationButton: {
    width: "36px",
    height: "36px",
    borderRadius: "999px",
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    fontSize: "18px",
    fontWeight: 700,
    cursor: "pointer",
  },
  branchKioskStationValue: {
    minWidth: "48px",
    padding: "8px 10px",
    borderRadius: "999px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    fontWeight: 800,
  },
  branchKioskAccessPanel: {
    display: "grid",
    gap: "8px",
    paddingTop: "8px",
    borderTop: "1px dashed rgba(154, 52, 18, 0.18)",
  },
  branchDisplayPanel: {
    display: "grid",
    gap: "10px",
    marginTop: "8px",
    paddingTop: "12px",
    borderTop: "1px dashed rgba(154, 52, 18, 0.18)",
  },
  branchDisplayCode: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    minHeight: "40px",
    padding: "8px 12px",
    borderRadius: "12px",
    border: "1px solid #fed7aa",
    backgroundColor: "#ffffff",
    color: "#7c2d12",
    fontWeight: 800,
    letterSpacing: "0.06em",
  },
  branchMenuBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "90px",
    padding: "7px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
  },
  branchMenuBadgeShared: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  branchMenuBadgeCustom: {
    backgroundColor: "#ecfdf5",
    color: "#047857",
  },
  branchMenuBadgeLoading: {
    backgroundColor: "#f1f5f9",
    color: "#64748b",
  },
  branchKitchenBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "90px",
    padding: "7px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    backgroundColor: "#ffedd5",
    color: "#c2410c",
  },
  itemTitle: {
    fontWeight: 700,
    color: "#0f172a",
  },
  itemMeta: {
    marginTop: "4px",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  itemBodyText: {
    color: "#334155",
    lineHeight: 1.5,
  },
  itemFooter: {
    color: "#64748b",
    fontSize: "13px",
  },
  tag: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 700,
  },
  permissionsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  infoInlineBand: {
    borderRadius: "14px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    padding: "12px 14px",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    fontWeight: 700,
  },
  secondaryMiniButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  secondaryMiniButtonActive: {
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  dangerMiniButton: {
    border: "1px solid #fecaca",
    borderRadius: "10px",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  ownerLockTag: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 10px",
    borderRadius: "999px",
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #bbf7d0",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  emptyState: {
    border: "1px dashed #cbd5e1",
    borderRadius: "16px",
    padding: "22px",
    color: "#64748b",
    backgroundColor: "#ffffff",
  },
  kioskLauncherOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1200,
    backgroundColor: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  kioskLauncherDialog: {
    width: "min(1080px, 100%)",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    display: "grid",
    gap: "18px",
    padding: "24px",
    borderRadius: "24px",
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.25)",
  },
  kioskLauncherHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },
  kioskLauncherEyebrow: {
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  kioskLauncherTitle: {
    margin: "6px 0 0",
    color: "#0f172a",
    fontSize: "30px",
    lineHeight: 1.05,
  },
  kioskLauncherGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 360px) minmax(0, 1fr)",
    gap: "18px",
  },
  kioskLauncherQrPanel: {
    display: "grid",
    gap: "10px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe3ee",
    backgroundColor: "#f8fafc",
  },
  kioskLauncherQrFrame: {
    width: "fit-content",
    padding: "18px",
    borderRadius: "18px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#ffffff",
  },
  kioskLauncherMiniQrFrame: {
    width: "fit-content",
    padding: "12px",
    borderRadius: "16px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#ffffff",
  },
  kioskLauncherInstructions: {
    display: "grid",
    gap: "12px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe3ee",
    backgroundColor: "#fff7ed",
  },
  kioskLauncherSteps: {
    margin: 0,
    paddingLeft: "20px",
    color: "#7c2d12",
    lineHeight: 1.7,
    display: "grid",
    gap: "6px",
  },
  kioskLauncherCallout: {
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid #fed7aa",
    backgroundColor: "#ffffff",
    color: "#9a3412",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  kioskLauncherStationSection: {
    display: "grid",
    gap: "12px",
  },
  kioskLauncherStationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  kioskLauncherStationCard: {
    display: "grid",
    gap: "10px",
    padding: "16px",
    borderRadius: "18px",
    border: "1px solid #dbe3ee",
    backgroundColor: "#f8fafc",
    justifyItems: "start",
  },
  kioskLauncherStationTitle: {
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 800,
  },
};

export default Configuracion;
